/**
 * PHI Scrubber — Name Detection Module (v2 — Name Region Parser)
 *
 * Two-phase architecture:
 *   1. Trigger Detection — identifies positions where a name begins
 *   2. Name Region Parser — walks forward from trigger, consuming the full
 *      name boundary including initials, particles, prefixes, suffixes,
 *      hyphens, apostrophes, and possessives
 *
 * Processing order (4 passes):
 *   Pass 0: Lock structural elements (section headers, labels, abbreviations)
 *   Pass 1: Trigger-based detection → Name Region Parser → record spans
 *   Pass 2: Known names propagation with eponymous context resolution
 *   Pass 3: [[NAME]] adjacency cleanup
 *
 * Public API (unchanged):
 *   scrubText, scrubPHI, scrubNames, containsObviousPHI, checkScrubberHealth
 */

import nlp from "compromise";

// ============================================================================
// Types
// ============================================================================

interface Span {
  start: number;
  end: number; // exclusive
}

/** Propagation confidence tiers for known names */
const TIER_PATIENT = 1; // Patient-origin: propagate freely
const TIER_RELATIONSHIP = 2; // Relationship-introduced: light context check
const TIER_STANDARD = 3; // Standard: full common-word context check

/** Map of name → highest confidence tier */
type TieredNames = Map<string, number>;

// ============================================================================
// Constants
// ============================================================================

const NAME_PLACEHOLDER = "[[NAME]]";
const FACILITY_PLACEHOLDER = "[[FACILITY]]";

// ---------------------------------------------------------------------------
// Titles — absorbed into the name span (trigger + consumed)
// ---------------------------------------------------------------------------

const TITLES = [
  "Dr",
  "Doctor",
  "Mr",
  "Mrs",
  "Ms",
  "Miss",
  "Prof",
  "Professor",
  "Nurse",
  "Rev",
  "Reverend",
  "Father",
  "Pastor",
  "Deacon",
  "Sheikh",
  "Rabbi",
  "Imam",
  "Sister",
  "Brother",
  "Venerable",
  "Monsignor",
  "Archbishop",
  "Cardinal",
  "Swami",
  "Lama",
  "Rinpoche",
  "Cantor",
  "Hon",
  "Honorable",
  "Sen",
  "Senator",
  "Rep",
  "Representative",
];
const TITLE_SET = new Set(TITLES.map((t) => t.toLowerCase()));

// ---------------------------------------------------------------------------
// Role descriptors — preserved in output; only the name after them is scrubbed
// Longest-first for greedy regex matching.
// ---------------------------------------------------------------------------

const ROLE_DESCRIPTORS: string[] = [
  "occupational therapist assistant",
  "certified diabetes educator",
  "certified cardiac rehab specialist",
  "certified pulmonary rehab specialist",
  "certified brain injury specialist",
  "certified hospice and palliative care nurse",
  "certified genetic counselor",
  "certified medical assistant",
  "certified athletic trainer",
  "certified hand therapist",
  "certified nursing assistant",
  "licensed clinical social worker",
  "licensed vocational nurse",
  "licensed practical nurse",
  "licensed acupuncturist",
  "behavioral health specialist",
  "speech-language pathologist",
  "speech language pathologist",
  "registered dietitian",
  "registered nurse",
  "physical therapist",
  "occupational therapist",
  "speech therapist",
  "speech pathologist",
  "respiratory therapist",
  "behavioral therapist",
  "radiation therapist",
  "pharmacy technician",
  "pharmacy tech",
  "pharmacy intern",
  "clinical pharmacist",
  "surgical technician",
  "surgical technologist",
  "surgical tech",
  "surgical assistant",
  "surgical scheduler",
  "surgical coordinator",
  "radiology technician",
  "radiology technologist",
  "radiology tech",
  "lab technician",
  "lab tech",
  "RN care coordinator",
  "RN coordinator",
  "RN medication nurse",
  "UM coordinator",
  "UM analyst",
  "clinical coordinator",
  "care coordinator",
  "intake coordinator",
  "intake specialist",
  "intake nurse",
  "transplant coordinator",
  "discharge planner",
  "case manager",
  "unit secretary",
  "unit clerk",
  "social worker intern",
  "social worker",
  "clinical social worker",
  "physician assistant",
  "nurse practitioner",
  "medical student",
  "medical assistant",
  "medical director",
  "medical interpreter",
  "medical scribe",
  "PA student",
  "PharmD student",
  "pharmacy student",
  "chief resident",
  "attending physician",
  "fellow physician",
  "home health aide",
  "home health nurse",
  "community health worker",
  "dental hygienist",
  "dental assistant",
  "athletic trainer",
  "utilization reviewer",
  "utilization review nurse",
  "peer reviewer",
  "peer counselor",
  "peer support specialist",
  "prior authorization specialist",
  "precertification specialist",
  "wound care nurse",
  "wound care specialist",
  "lactation consultant",
  "lactation specialist",
  "child life specialist",
  "genetic counselor",
  "genetics counselor",
  "patient navigator",
  "patient care technician",
  "patient care tech",
  "ophthalmic technician",
  "ophthalmic tech",
  "cardiac monitor tech",
  "monitor tech",
  "bone density technologist",
  "claims liaison",
  "claims rep",
  "charge nurse",
  "floor nurse",
  "triage nurse",
  "pre-op nurse",
  "nurse injector",
  "nursing student",
  "infusion nurse",
  "infusion RN",
  "medication nurse",
  "charge RN",
  "triage RN",
  "NICU RN coordinator",
  "NICU RN",
  "certified OB sonographer",
  "certified asthma educator",
  "certified nurse-midwife",
  "certified lactation consultant",
  "certified dementia practitioner",
  "licensed genetic counselor",
  "certified EEG technologist",
  "EEG technologist",
  "yoga instructor",
  "personal trainer",
  "fitness instructor",
  "meditation teacher",
  "Pilates instructor",
  "nurse manager",
  "nurse educator",
  "nurse anesthetist",
  "nurse midwife",
  "anesthesiologist",
  "cardiologist",
  "chiropractor",
  "coordinator",
  "counselor",
  "dermatologist",
  "dietitian",
  "dietician",
  "doula",
  "endocrinologist",
  "gastroenterologist",
  "gynecologist",
  "hospitalist",
  "internist",
  "midwife",
  "naturopath",
  "neonatologist",
  "nephrologist",
  "neurologist",
  "nutritionist",
  "obstetrician",
  "oncologist",
  "ophthalmologist",
  "optometrist",
  "orthopedist",
  "otolaryngologist",
  "paramedic",
  "pathologist",
  "pediatrician",
  "phlebotomist",
  "physiatrist",
  "psychiatrist",
  "psychologist",
  "pulmonologist",
  "radiologist",
  "rheumatologist",
  "scribe",
  "sonographer",
  "surgeon",
  "therapist",
  "technician",
  "technologist",
  "urologist",
  "interpreter",
  "RN",
  "MA",
  "PA",
  "NP",
  "LPN",
  "LVN",
  "PT",
  "OT",
  "ST",
  "RT",
  "RD",
  "SW",
  "CRNA",
  "CNA",
  "DPM",
  "DO",
  "MD",
  "DPT",
  "PharmD",
  "PsyD",
  "PhD",
  "ATC",
  "LAT",
  "EMT",
  "LCSW",
  "LSW",
  "MSW",
  "SLP",
];
const ROLE_DESCRIPTORS_SORTED = [...ROLE_DESCRIPTORS].sort(
  (a, b) => b.length - a.length
);

// ---------------------------------------------------------------------------
// Protected abbreviations — skip entirely during name detection
// ---------------------------------------------------------------------------

const PROTECTED_ABBREVIATIONS = new Set([
  "BID",
  "TID",
  "QID",
  "QHS",
  "PRN",
  "AC",
  "PC",
  "HS",
  "QD",
  "QOD",
  "SC",
  "IM",
  "IV",
  "IO",
  "PO",
  "SL",
  "PR",
  "IR",
  "ER",
  "XR",
  "SR",
  "CR",
  "LA",
  "SA",
  "ED",
  "OR",
  "ICU",
  "NICU",
  "PICU",
  "PACU",
  "CCU",
  "OB",
  "GI",
  "GU",
  "ENT",
  "MRI",
  "MRA",
  "CXR",
  "CT",
  "US",
  "UA",
  "PET",
  "EMG",
  "EEG",
  "ECG",
  "EKG",
  "ECHO",
  "EF",
  "LV",
  "RV",
  "BP",
  "HR",
  "RR",
  "CBC",
  "CMP",
  "BMP",
  "LFTs",
  "TSH",
  "HbA1c",
  "BNP",
  "INR",
  "PSA",
  "PHQ",
  "HTN",
  "T2DM",
  "T1DM",
  "DM",
  "CKD",
  "CAD",
  "CHF",
  "HF",
  "COPD",
  "OSA",
  "GERD",
  "DVT",
  "PE",
  "CVA",
  "TIA",
  "TBI",
  "PCOS",
  "RA",
  "OA",
  "AVN",
  "FMLA",
  "SpO2",
  "BMI",
  "MR",
  "AR",
  "TR",
  "AS",
  "MS",
  "AI",
  "YMCA",
  "HIPAA",
  "CABG",
  "VATS",
  "LVAD",
  "BiPAP",
  "CPAP",
  "CGM",
  "ADLs",
  "EMR",
  "EHR",
  "OTC",
  "AA",
  "FaceTime",
  "CoQ10",
  "PFTs",
  "HOPE",
  "NC",
  // Clinical role abbreviations — also in ROLE_DESCRIPTORS but listed here
  // so the parser's abbreviation check catches them before initial-matching.
  // This prevents two-letter role tokens from being consumed as names.
  "RN",
  "MA",
  "PA",
  "NP",
  "LPN",
  "LVN",
  "PT",
  "OT",
  "ST",
  "RT",
  "RD",
  "SW",
  "CRNA",
  "CNA",
  "DPM",
  "DO",
  "MD",
  "DPT",
  "EMT",
  "LCSW",
  "LSW",
  "MSW",
  "SLP",
  "ATC",
  "LAT",
]);

// ---------------------------------------------------------------------------
// Structural labels — locked before name detection
// ---------------------------------------------------------------------------

const PROTECTED_LABELS = [
  "referring physician",
  "attending physician",
  "consulting physician",
  "treating physician",
  "ordering physician",
  "supervising physician",
  "primary care physician",
  "primary care provider",
  "referring provider",
  "attending provider",
  "supervising provider",
  "electronically signed by",
  "e-signed by",
  "cosigned by",
  "co-signed by",
  "documented by",
  "reviewed by",
  "signed by",
  "ordered by",
  "dictated by",
  "transcribed by",
  "authenticated by",
  "approved by",
  "verified by",
  "addended by",
  "amended by",
  "supervised by",
  "evaluated by",
  "examined by",
  "seen by",
  "treated by",
  "referred by",
  "performed by",
  "administered by",
  "prescribed by",
  "managed by",
  "coordinated by",
  "confirmed by",
  "witnessed by",
  "attending",
  "supervising",
  "physician",
  "provider",
  "consultant",
  // MRN/account labels — prevent "Patient Account" from triggering name detection
  "patient account",
  "patient id",
  "account number",
  "account no",
  "chart number",
  "chart no",
  "medical record number",
  "medical record no",
  "medical rec",
  "med rec",
  "mr number",
];

const NAME_LABELS = [
  "Referring physician",
  "Referring provider",
  "Attending physician",
  "Attending provider",
  "Primary care physician",
  "Primary care provider",
  "Treating physician",
  "Ordering physician",
  "Consulting physician",
  "Supervising physician",
  "Supervising provider",
  "Emergency contact",
  "Authorized representative",
  "Responsible party",
  "Consultant",
  "Attending",
  "Supervising",
  "Physician",
  "Provider",
  "Patient",
  "Name",
  "Pt",
];

const ACTION_BY_LABELS = [
  "cosigned by",
  "co-signed by",
  "reviewed by",
  "signed by",
  "ordered by",
  "dictated by",
  "transcribed by",
  "authenticated by",
  "approved by",
  "verified by",
  "addended by",
  "amended by",
  "electronically signed by",
  "e-signed by",
  "evaluated by",
  "examined by",
  "seen by",
  "treated by",
  "referred by",
  "supervised by",
  "documented by",
  "performed by",
  "administered by",
  "prescribed by",
  "managed by",
  "coordinated by",
  "confirmed by",
  "witnessed by",
];

// ---------------------------------------------------------------------------
// Nobiliary particles — lowercase connectors inside surnames
// ---------------------------------------------------------------------------

const PARTICLES = [
  "de la",
  "de las",
  "de los",
  "van der",
  "van den",
  "van de",
  "von der",
  "von den",
  "del",
  "de",
  "di",
  "da",
  "von",
  "van",
  "le",
  "la",
  "el",
  "al",
  "bin",
  "ibn",
  "ben",
];

// ---------------------------------------------------------------------------
// Name suffixes and professional suffixes
// ---------------------------------------------------------------------------

const NAME_SUFFIXES = new Set(["jr", "sr", "ii", "iii", "iv", "v", "esq"]);
const PROFESSIONAL_SUFFIXES = new Set([
  "md",
  "do",
  "nd",
  "phd",
  "dds",
  "dmd",
  "dpm",
  "od",
  "pharmd",
  "psyd",
  "dnp",
  "msn",
  "mph",
]);

// ---------------------------------------------------------------------------
// Clinical whitelist — eponymous terms needing context resolution
// ---------------------------------------------------------------------------

const CLINICAL_WHITELIST = new Set([
  "abdomen",
  "abdominal",
  "anterior",
  "bilateral",
  "bowel",
  "breast",
  "cardiac",
  "carpal",
  "central",
  "cerebellar",
  "cerebral",
  "cervical",
  "chest",
  "clavicle",
  "colon",
  "cranial",
  "dermal",
  "distal",
  "dorsal",
  "elbow",
  "epidural",
  "femoral",
  "femur",
  "flexor",
  "frontal",
  "gastric",
  "gluteal",
  "hepatic",
  "humeral",
  "iliac",
  "inguinal",
  "intercostal",
  "kidney",
  "lateral",
  "liver",
  "lumbar",
  "medial",
  "median",
  "meningeal",
  "metacarpal",
  "occipital",
  "orbital",
  "parietal",
  "patellar",
  "pelvic",
  "pelvis",
  "peroneal",
  "plantar",
  "pleural",
  "popliteal",
  "posterior",
  "proximal",
  "pulmonary",
  "radial",
  "renal",
  "sacral",
  "sacroiliac",
  "scapular",
  "shoulder",
  "spinal",
  "sternum",
  "subcutaneous",
  "superior",
  "temporal",
  "thoracic",
  "tibial",
  "tracheal",
  "ulnar",
  "uterine",
  "vascular",
  "venous",
  "ventral",
  "vertebral",
  "wrist",
  "alzheimer",
  "angina",
  "arthritis",
  "asthma",
  "carcinoma",
  "cellulitis",
  "colitis",
  "crohn",
  "cushing",
  "delirium",
  "dementia",
  "dermatitis",
  "diabetes",
  "diverticulitis",
  "edema",
  "embolism",
  "encephalopathy",
  "endometriosis",
  "epilepsy",
  "fibrillation",
  "fibromyalgia",
  "gout",
  "hashimoto",
  "hernia",
  "hodgkin",
  "hypertension",
  "hypothyroidism",
  "infarction",
  "ischemia",
  "leukemia",
  "lymphoma",
  "melanoma",
  "meningitis",
  "migraine",
  "myeloma",
  "neuropathy",
  "osteoarthritis",
  "osteoporosis",
  "pancreatitis",
  "parkinson",
  "pneumonia",
  "psoriasis",
  "raynaud",
  "sarcoidosis",
  "scoliosis",
  "sepsis",
  "stenosis",
  "thrombosis",
  "tinnitus",
  "acetaminophen",
  "adalimumab",
  "albuterol",
  "amlodipine",
  "amoxicillin",
  "aspirin",
  "atorvastatin",
  "azithromycin",
  "baclofen",
  "budesonide",
  "buprenorphine",
  "carbamazepine",
  "cephalexin",
  "ciprofloxacin",
  "citalopram",
  "clonazepam",
  "clonidine",
  "cyclobenzaprine",
  "diazepam",
  "diclofenac",
  "duloxetine",
  "escitalopram",
  "fentanyl",
  "fluoxetine",
  "gabapentin",
  "hydrochlorothiazide",
  "hydrocodone",
  "hydroxychloroquine",
  "ibuprofen",
  "insulin",
  "ketamine",
  "ketorolac",
  "lamotrigine",
  "lisinopril",
  "lorazepam",
  "meloxicam",
  "metformin",
  "methadone",
  "methotrexate",
  "metoprolol",
  "morphine",
  "naproxen",
  "omeprazole",
  "ondansetron",
  "oxycodone",
  "pantoprazole",
  "prednisone",
  "pregabalin",
  "propranolol",
  "sertraline",
  "sumatriptan",
  "tamsulosin",
  "tizanidine",
  "topiramate",
  "tramadol",
  "trazodone",
  "venlafaxine",
  "warfarin",
  "zolpidem",
  "ablation",
  "angiography",
  "arthroplasty",
  "arthroscopy",
  "biopsy",
  "catheterization",
  "cholecystectomy",
  "colectomy",
  "colonoscopy",
  "craniotomy",
  "debridement",
  "discectomy",
  "echocardiogram",
  "electromyography",
  "endoscopy",
  "fasciotomy",
  "fluoroscopy",
  "fusion",
  "hysterectomy",
  "injection",
  "intubation",
  "laparoscopy",
  "laminectomy",
  "mammography",
  "mastectomy",
  "mri",
  "nephrectomy",
  "neurostimulator",
  "osteotomy",
  "radiograph",
  "resection",
  "thoracotomy",
  "ultrasound",
  "acute",
  "benign",
  "chronic",
  "clinical",
  "congenital",
  "contralateral",
  "degenerative",
  "diffuse",
  "etiology",
  "exacerbation",
  "focal",
  "hereditary",
  "idiopathic",
  "inflammatory",
  "intermittent",
  "ipsilateral",
  "malignant",
  "metastatic",
  "moderate",
  "multifocal",
  "neoplasm",
  "nosocomial",
  "palliative",
  "pathology",
  "prognosis",
  "progressive",
  "prophylaxis",
  "recurrent",
  "refractory",
  "remission",
  "severe",
  "subluxation",
  "symptomatic",
  "syndrome",
  "systemic",
  "apgar",
  "apache",
  "sofa",
  "wells",
  "morse",
  "braden",
  "norton",
  "barthel",
  "karnofsky",
  "rankin",
  "fisher",
  "hunt",
  "hess",
  "child",
  "pugh",
  "meld",
  "timi",
  "grace",
  "chads",
  "bishop",
  "glasgow",
  "mallampati",
  "nyha",
  "valsalva",
  "may",
  "will",
  "mark",
  "grant",
  "faith",
  "hope",
  "joy",
  "dawn",
  "summer",
  "august",
  "april",
  "june",
  "jean",
  "frank",
  "bill",
  "robin",
  "penny",
  "ray",
  "pat",
  "glen",
  "dale",
  "ward",
  "lane",
  "chase",
  "sterling",
  "reed",
  "cross",
  "hardy",
  "rep",
  "hawkins",
  "kennedy",
  "neer",
  "spurling",
  "lachman",
  "mcmurray",
  "babinski",
  "romberg",
  "trendelenburg",
  "phalen",
  "tinel",
  "hoffmann",
  "hoffman",
  "lasegue",
  "kernig",
  "brudzinski",
  "murphy",
  "mcburney",
  "rovsing",
  "psoas",
  "obturator",
  "allen",
  "adson",
  "finkelstein",
  "froment",
  "jobe",
  "speed",
  "yergason",
  "appley",
  "apley",
  "thessaly",
  "ober",
  "thomas",
  "faber",
  "patrick",
  "gaenslen",
  "slump",
  "dix",
  "hallpike",
  "epley",
  "barlow",
  "ortolani",
  "galeazzi",
  "barton",
  "colles",
  "smith",
  "bennett",
  "dupuytren",
  "heberden",
  "bouchard",
  "swan",
  "boutonniere",
  "lhermitte",
  "rinne",
  "weber",
  "schober",
  "waddell",
  "achilles",
  "bell",
  "thompson",
  "drawer",
  "pivot",
  "valgus",
  "varus",
  // Additional eponymous — standalone possessive references
  "todd",
  "addison",
  "graves",
  "sjögren",
  "sjogren",
  "broca",
  "wernicke",
  "spetzler",
  "martin",
  // Multi-word / additional eponymous terms
  "grey",
  "turner",
  "cullen",
  "courvoisier",
  "chvostek",
  "trousseau",
  "battle",
  "kehr",
  "blumberg",
  "carnett",
  "dance",
  "ransohoff",
  "chandelier",
  "homan",
  "homans",
  "joseph",
]);

// ---------------------------------------------------------------------------
// Eponymous context resolution
// ---------------------------------------------------------------------------

const CLINICAL_CONTEXT_AFTER = new Set([
  "negative",
  "positive",
  "test",
  "tests",
  "testing",
  "sign",
  "signs",
  "maneuver",
  "maneuvers",
  "reflex",
  "reflexes",
  "syndrome",
  "syndromes",
  "technique",
  "techniques",
  "procedure",
  "procedures",
  "score",
  "scores",
  "classification",
  "classifications",
  "criteria",
  "grade",
  "grading",
  "stage",
  "staging",
  "disease",
  "diseases",
  "disorder",
  "disorders",
  "scale",
  "scales",
  "law",
  "rule",
  "fracture",
  "fractures",
  "lesion",
  "lesions",
  "palsy",
  "paralysis",
  "contracture",
  "contractures",
  "deformity",
  "deformities",
  "anomaly",
  "anomalies",
  "hernia",
  "hernias",
  "node",
  "nodes",
  "phenomenon",
  "impingement",
  "method",
  "methods",
  "operation",
  "repair",
  "approach",
  "protocol",
  "triad",
  "equation",
  "ratio",
  "index",
  "stain",
  "bodies",
  "cells",
  "duct",
  "canal",
  "ligament",
  "fascia",
  "plexus",
  "fossa",
  "line",
  "point",
  "triangle",
  "space",
  "zone",
  "angle",
  "tubercle",
  "not",
  "deferred",
  "bilaterally",
  "indicated",
]);

const CLINICAL_CONTEXT_BEFORE = new Set([
  "positive",
  "negative",
  "modified",
  "reverse",
  "bilateral",
  "unilateral",
  "normal",
  "abnormal",
  "absent",
  "present",
  "intact",
  "decreased",
  "increased",
  "reduced",
  "no",
]);

const PERSON_CONTEXT_AFTER = new Set([
  "assisted",
  "reviewed",
  "documented",
  "coordinated",
  "created",
  "provided",
  "obtained",
  "administered",
  "ordered",
  "called",
  "evaluated",
  "examined",
  "assessed",
  "noted",
  "reported",
  "completed",
  "performed",
  "recommended",
  "discussed",
  "confirmed",
  "contacted",
  "notified",
  "signed",
  "approved",
  "verified",
  "prepared",
  "started",
  "stopped",
  "continued",
  "returned",
  "presented",
  "denied",
  "complained",
  "stated",
  "mentioned",
  "indicated",
  "requested",
  "from",
  "at",
  "to",
  "is",
  "was",
  "will",
  "has",
  "had",
  "who",
]);

// ---------------------------------------------------------------------------
// Relationship descriptors
// ---------------------------------------------------------------------------

const RELATIONSHIP_DESCRIPTORS = [
  "mother",
  "father",
  "mom",
  "dad",
  "daughter",
  "son",
  "sister",
  "brother",
  "wife",
  "husband",
  "spouse",
  "partner",
  "grandmother",
  "grandfather",
  "grandma",
  "grandpa",
  "aunt",
  "uncle",
  "niece",
  "nephew",
  "cousin",
  "stepmother",
  "stepfather",
  "stepdaughter",
  "stepson",
  "mother-in-law",
  "father-in-law",
  "daughter-in-law",
  "son-in-law",
  "sister-in-law",
  "brother-in-law",
  "caregiver",
  "guardian",
  "legal guardian",
  "emergency contact",
  "power of attorney",
  "next of kin",
  "significant other",
  "fiancé",
  "fiancee",
  "fiance",
  "fiancée",
  "boyfriend",
  "girlfriend",
  "roommate",
  "neighbor",
  "neighbour",
  "friend",
  "employer",
  "pastor",
  "priest",
  "imam",
];

// ---------------------------------------------------------------------------
// Geographic protection
// ---------------------------------------------------------------------------

const COUNTRIES = new Set([
  "afghanistan",
  "albania",
  "algeria",
  "argentina",
  "armenia",
  "australia",
  "austria",
  "azerbaijan",
  "bahamas",
  "bangladesh",
  "barbados",
  "belarus",
  "belgium",
  "belize",
  "benin",
  "bhutan",
  "bolivia",
  "bosnia",
  "botswana",
  "brazil",
  "brunei",
  "bulgaria",
  "burkina",
  "burundi",
  "cambodia",
  "cameroon",
  "canada",
  "chad",
  "chile",
  "china",
  "colombia",
  "comoros",
  "congo",
  "croatia",
  "cuba",
  "cyprus",
  "czechia",
  "denmark",
  "djibouti",
  "dominica",
  "ecuador",
  "egypt",
  "england",
  "eritrea",
  "estonia",
  "eswatini",
  "ethiopia",
  "fiji",
  "finland",
  "france",
  "gabon",
  "gambia",
  "georgia",
  "germany",
  "ghana",
  "greece",
  "grenada",
  "guatemala",
  "guinea",
  "guyana",
  "haiti",
  "honduras",
  "hungary",
  "iceland",
  "india",
  "indonesia",
  "iran",
  "iraq",
  "ireland",
  "israel",
  "italy",
  "jamaica",
  "japan",
  "jordan",
  "kazakhstan",
  "kenya",
  "korea",
  "kosovo",
  "kuwait",
  "laos",
  "latvia",
  "lebanon",
  "lesotho",
  "liberia",
  "libya",
  "liechtenstein",
  "lithuania",
  "luxembourg",
  "madagascar",
  "malawi",
  "malaysia",
  "maldives",
  "mali",
  "malta",
  "mauritania",
  "mauritius",
  "mexico",
  "moldova",
  "monaco",
  "mongolia",
  "montenegro",
  "morocco",
  "mozambique",
  "myanmar",
  "namibia",
  "nauru",
  "nepal",
  "netherlands",
  "nicaragua",
  "niger",
  "nigeria",
  "norway",
  "oman",
  "pakistan",
  "palau",
  "palestine",
  "panama",
  "paraguay",
  "peru",
  "philippines",
  "poland",
  "portugal",
  "qatar",
  "romania",
  "russia",
  "rwanda",
  "samoa",
  "scotland",
  "senegal",
  "serbia",
  "singapore",
  "slovakia",
  "slovenia",
  "somalia",
  "spain",
  "sudan",
  "suriname",
  "sweden",
  "switzerland",
  "syria",
  "taiwan",
  "tajikistan",
  "tanzania",
  "thailand",
  "togo",
  "tonga",
  "trinidad",
  "tunisia",
  "turkey",
  "turkmenistan",
  "tuvalu",
  "uganda",
  "ukraine",
  "uruguay",
  "uzbekistan",
  "vanuatu",
  "venezuela",
  "vietnam",
  "wales",
  "yemen",
  "zambia",
  "zimbabwe",
  "united",
  "south",
  "north",
  "central",
  "saudi",
  "sri",
  "new",
  "costa",
  "el",
  "sierra",
  "equatorial",
  "dominican",
  "czech",
  "ivory",
]);

const US_STATES = new Set([
  "alabama",
  "alaska",
  "arizona",
  "arkansas",
  "california",
  "colorado",
  "connecticut",
  "delaware",
  "florida",
  "georgia",
  "hawaii",
  "idaho",
  "illinois",
  "indiana",
  "iowa",
  "kansas",
  "kentucky",
  "louisiana",
  "maine",
  "maryland",
  "massachusetts",
  "michigan",
  "minnesota",
  "mississippi",
  "missouri",
  "montana",
  "nebraska",
  "nevada",
  "new hampshire",
  "new jersey",
  "new mexico",
  "new york",
  "north carolina",
  "north dakota",
  "ohio",
  "oklahoma",
  "oregon",
  "pennsylvania",
  "rhode island",
  "south carolina",
  "south dakota",
  "tennessee",
  "texas",
  "utah",
  "vermont",
  "virginia",
  "washington",
  "west virginia",
  "wisconsin",
  "wyoming",
  "al",
  "ak",
  "az",
  "ar",
  "ca",
  "co",
  "ct",
  "de",
  "fl",
  "ga",
  "hi",
  "id",
  "il",
  "in",
  "ia",
  "ks",
  "ky",
  "la",
  "me",
  "md",
  "ma",
  "mi",
  "mn",
  "ms",
  "mo",
  "mt",
  "ne",
  "nv",
  "nh",
  "nj",
  "nm",
  "ny",
  "nc",
  "nd",
  "oh",
  "ok",
  "or",
  "pa",
  "ri",
  "sc",
  "sd",
  "tn",
  "tx",
  "ut",
  "vt",
  "va",
  "wa",
  "wv",
  "wi",
  "wy",
  "dc",
]);

const LOCATION_PREPOSITIONS = new Set([
  "in",
  "from",
  "to",
  "at",
  "near",
  "outside",
  "within",
]);

// ---------------------------------------------------------------------------
// PE sub-section labels — protected from name detection
// ---------------------------------------------------------------------------

const PE_SUBLABELS = new Set([
  "general",
  "heent",
  "cv",
  "cardiovascular",
  "lungs",
  "pulmonary",
  "abdomen",
  "abdominal",
  "extremities",
  "neuro",
  "neurological",
  "skin",
  "musculoskeletal",
  "msk",
  "psychiatric",
  "psych",
  "lymph",
  "vascular",
  "gu",
  "genitourinary",
  "rectal",
  "breast",
  "back",
  "neck",
  "head",
  "eyes",
  "ears",
  "nose",
  "throat",
  "chest",
  "pelvic",
  "dental",
  "mental status",
  "vitals",
  "vital signs",
  "constitutional",
]);

// ---------------------------------------------------------------------------
// Facility suffix terms — when a capitalized word precedes one of these,
// the sequence is a facility reference, not a person name.
// ---------------------------------------------------------------------------

const FACILITY_SUFFIXES = new Set([
  "general",
  "memorial",
  "regional",
  "university",
  "community",
  "medical center",
  "medical centre",
  "health center",
  "health centre",
  "hospital",
  "hospitals",
  "clinic",
  "clinics",
  "rehabilitation",
  "associates",
  "foundation",
  "institute",
  "center",
  "centre",
  "hematology",
  "oncology",
  "cardiology",
  "neurology",
  "radiology",
  "orthopedics",
  "pediatrics",
  "obstetrics",
  "gynecology",
  "urology",
  "dermatology",
  "ophthalmology",
  "pathology",
  "psychiatry",
  "surgery",
  "surgical",
  "pharmacy",
  "laboratory",
  "laboratories",
  "imaging",
  "emergency",
  "urgent care",
  "outpatient",
  "inpatient",
  "maternity",
  "birthing",
  "neonatal",
  "hospice",
  "palliative",
  // Organizational suffixes (Fix 6)
  "partners",
  "group",
  "network",
  "alliance",
  "coalition",
  "society",
  "authority",
  "agency",
  "department",
  "bureau",
  "office",
  "studio",
  "gallery",
  "gym",
  "sanctuary",
  "church",
  "synagogue",
  "mosque",
  "temple",
  "academy",
  "college",
  // Abbreviated facility/unit terms
  "ed",
  "er",
  "icu",
  "nicu",
  "picu",
  "pacu",
  "ccu",
  "or",
  "l&d",
  "snf",
  "alf",
  "ltc",
  "asc",
]);

// ---------------------------------------------------------------------------
// Insurer names — locked before name detection (highest priority)
// ---------------------------------------------------------------------------

const INSURER_NAMES = [
  "anthem blue cross",
  "anthem blue cross blue shield",
  "anthem",
  "blue cross blue shield",
  "blue cross",
  "blue shield",
  "unitedhealth",
  "unitedhealthcare",
  "united healthcare",
  "united health",
  "optum",
  "aetna",
  "cvs health",
  "cigna",
  "evernorth",
  "humana",
  "centene",
  "wellcare",
  "ambetter",
  "molina healthcare",
  "molina",
  "kaiser permanente",
  "kaiser",
  "tricare",
  "champva",
  "state farm",
  "geico",
  "allstate",
  "progressive",
  "medicaid",
  "medicare",
  "medi-cal",
  "magellan health",
  "magellan",
  "beacon health",
  "carelon",
];

// ---------------------------------------------------------------------------
// Multi-word city names — protected from name detection
// ---------------------------------------------------------------------------

const MULTI_WORD_CITIES = [
  "ho chi minh city",
  "new york city",
  "mexico city",
  "salt lake city",
  "kansas city",
  "oklahoma city",
  "atlantic city",
  "jersey city",
  "panama city",
  "rapid city",
  "iowa city",
  "lake city",
  "san francisco",
  "san jose",
  "san diego",
  "san antonio",
  "san juan",
  "san salvador",
  "san marino",
  "santa fe",
  "santa barbara",
  "santa monica",
  "santa cruz",
  "santa rosa",
  "los angeles",
  "las vegas",
  "el paso",
  "el salvador",
  "baton rouge",
  "des moines",
  "st. louis",
  "st. paul",
  "st. petersburg",
  "fort worth",
  "fort lauderdale",
  "fort collins",
  "fort wayne",
  "port arthur",
  "port charlotte",
  "new orleans",
  "new delhi",
  "new haven",
  "hong kong",
  "kuala lumpur",
  "buenos aires",
  "rio de janeiro",
  "sao paulo",
  "addis ababa",
  "dar es salaam",
  "cape town",
  "tel aviv",
  "abu dhabi",
  "sri jayawardenepura",
  "lake oswego",
  "lake charles",
  "lake havasu",
  "palm springs",
  "palm beach",
  "palm desert",
  "long beach",
  "virginia beach",
  "myrtle beach",
  "grand rapids",
  "cedar rapids",
  "sioux falls",
  "little rock",
  "north little rock",
  "corpus christi",
  "ann arbor",
];

// ---------------------------------------------------------------------------
// Low-confidence propagation — common English words that also appear as names
// These require supporting context in the propagation pass.
// ---------------------------------------------------------------------------

const COMMON_WORDS = new Set([
  // Articles / pronouns / determiners
  "the",
  "a",
  "an",
  "he",
  "she",
  "his",
  "her",
  "they",
  "them",
  "their",
  "its",
  "this",
  "that",
  "these",
  "those",
  "my",
  "your",
  "our",
  "me",
  "we",
  "us",
  "it",
  // Common verbs that could be names
  "may",
  "will",
  "grant",
  "chase",
  "wade",
  "pierce",
  "drew",
  "sue",
  "rob",
  "bob",
  "pat",
  "rod",
  "art",
  "bill",
  "don",
  "jimmy",
  "skip",
  "chuck",
  "nick",
  "mark",
  "rick",
  "josh",
  "dale",
  "wade",
  "dean",
  "terry",
  "gene",
  "lance",
  // Common nouns that double as names
  "grace",
  "hope",
  "faith",
  "joy",
  "mercy",
  "patience",
  "charity",
  "harmony",
  "destiny",
  "trinity",
  "angel",
  "crystal",
  "diamond",
  "pearl",
  "ruby",
  "amber",
  "ivy",
  "rose",
  "sage",
  "dawn",
  "summer",
  "autumn",
  "spring",
  "winter",
  "hunter",
  "mason",
  "carter",
  "cooper",
  "porter",
  "tanner",
  "parker",
  "walker",
  "fisher",
  "archer",
  "ranger",
  "major",
  "judge",
  "duke",
  "king",
  "prince",
  "queen",
  "bishop",
  "knight",
  "castle",
  "abbey",
  "brook",
  "glen",
  "dale",
  "heath",
  "marsh",
  "moor",
  "cliff",
  "ford",
  "ridge",
  "field",
  "stone",
  "ward",
  "lane",
  "banks",
  "bell",
  "young",
  "long",
  "cross",
  "rice",
  "berry",
  "reed",
  "park",
  "lee",
  "ray",
  "noble",
  "sterling",
  "hardy",
  "rich",
  "bland",
  // Short words easily confused
  "an",
  "ben",
  "bo",
  "ed",
  "jo",
  "al",
  "sol",
  "val",
  "max",
  "rex",
  "gus",
  "ash",
  "bay",
  "eve",
  "fern",
  "gale",
  "iris",
  "jade",
  "kit",
  "lark",
  "moss",
  "penn",
  "robin",
  "penny",
  "frank",
  "jean",
  "june",
  "april",
  "august",
]);

// ---------------------------------------------------------------------------
// Person-context patterns for low-confidence propagation
// ---------------------------------------------------------------------------

function hasPersonContext(
  text: string,
  span: Span,
  documentNames: TieredNames
): boolean {
  const spanText = text.slice(span.start, span.end);

  // Adjacent to [[NAME]] tag
  const before60 = text.slice(Math.max(0, span.start - 60), span.start);
  if (before60.trimEnd().endsWith(NAME_PLACEHOLDER)) return true;

  // After [[NAME]] tag with just whitespace between
  const after60 = text.slice(span.end, Math.min(text.length, span.end + 60));
  if (after60.trimStart().startsWith(NAME_PLACEHOLDER)) return true;

  // Adjacent to another known name component (e.g. "Grace Okonkwo")
  const beforeWords = before60.trim().split(/\s+/);
  const lastBefore = beforeWords[beforeWords.length - 1]?.replace(
    /[.,;:]+$/,
    ""
  );
  if (
    lastBefore &&
    documentNames.has(lastBefore) &&
    !COMMON_WORDS.has(lastBefore.toLowerCase())
  )
    return true;

  const afterWords = text
    .slice(span.end)
    .replace(/^[ \t]+/, "")
    .match(/^[A-Z][a-z]+/);
  if (
    afterWords &&
    documentNames.has(afterWords[0]) &&
    !COMMON_WORDS.has(afterWords[0].toLowerCase())
  )
    return true;

  // In a relationship pattern ("his wife Grace", "spoke with Grace")
  const beforeLower = before60.toLowerCase();
  for (const rel of [
    "wife",
    "husband",
    "mother",
    "father",
    "daughter",
    "son",
    "sister",
    "brother",
    "spouse",
    "partner",
    "aunt",
    "uncle",
    "friend",
    "neighbor",
    "fiancé",
    "fiancee",
  ]) {
    if (beforeLower.endsWith(rel + " ") || beforeLower.endsWith(rel + ", "))
      return true;
  }

  // After title
  if (lastBefore && TITLE_SET.has(lastBefore.toLowerCase().replace(/\.$/, "")))
    return true;

  // Preceded by person-context verb patterns ("spoke with Grace", "contacted Grace")
  const verbPattern =
    /(?:spoke|met|called|contacted|visited|notified)\s+(?:with\s+)?$/i;
  if (verbPattern.test(before60)) return true;

  // Followed by a person-context action verb ("Banks called", "Grace reviewed")
  // Only strong action verbs — not weak ones like "is"/"was"/"from" which could
  // produce false positives ("Grace is a virtue", "May was warm")
  const STRONG_PERSON_VERBS = new Set([
    "assisted",
    "reviewed",
    "documented",
    "coordinated",
    "created",
    "provided",
    "obtained",
    "administered",
    "ordered",
    "called",
    "evaluated",
    "examined",
    "assessed",
    "noted",
    "reported",
    "completed",
    "performed",
    "recommended",
    "discussed",
    "confirmed",
    "contacted",
    "notified",
    "signed",
    "approved",
    "verified",
    "prepared",
    "started",
    "stopped",
    "continued",
    "returned",
    "presented",
    "denied",
    "complained",
    "stated",
    "mentioned",
    "indicated",
    "requested",
  ]);
  const afterTrim = text.slice(span.end).replace(/^[ \t]+/, "");
  const nextWord = afterTrim.match(/^([a-zA-Z]+)/);
  if (nextWord && STRONG_PERSON_VERBS.has(nextWord[1].toLowerCase()))
    return true;

  return false;
}

// ============================================================================
// Utility Functions
// ============================================================================

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function mergeSpans(spans: Span[]): Span[] {
  if (spans.length === 0) return [];
  const sorted = [...spans].sort((a, b) => a.start - b.start || b.end - a.end);
  const merged: Span[] = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i++) {
    const cur = sorted[i];
    const last = merged[merged.length - 1];
    if (cur.start <= last.end) {
      last.end = Math.max(last.end, cur.end);
    } else {
      merged.push({ ...cur });
    }
  }
  return merged;
}

function replaceSpans(text: string, spans: Span[]): string {
  const sorted = [...spans].sort((a, b) => b.start - a.start);
  let result = text;
  for (const s of sorted) {
    result = result.slice(0, s.start) + NAME_PLACEHOLDER + result.slice(s.end);
  }
  return result;
}

function isInsideSectionHeader(text: string, pos: number): boolean {
  const lineStart = text.lastIndexOf("\n", pos - 1) + 1;
  const lineEnd = text.indexOf("\n", pos);
  const line = text.slice(lineStart, lineEnd === -1 ? text.length : lineEnd);
  return /^[A-Z][A-Z\s\/]+:/.test(line.trim());
}

function isProtectedPos(pos: number, spans: Span[]): boolean {
  for (const s of spans) {
    if (pos >= s.start && pos < s.end) return true;
    if (s.start > pos) break;
  }
  return false;
}

function isGeoLocation(text: string, end: number): boolean {
  const after = text.slice(end);
  const m = after.match(/^,\s*([A-Za-z][A-Za-z\s.]+)/);
  if (!m) return false;
  const loc = m[1].trim().toLowerCase().replace(/\./g, "");
  return (
    COUNTRIES.has(loc) ||
    COUNTRIES.has(loc.split(/\s+/)[0]) ||
    US_STATES.has(loc) ||
    (loc.length <= 2 && US_STATES.has(loc))
  );
}

// ============================================================================
// Pass 0 — Lock Structural Elements
// ============================================================================

/**
 * Find insurer name spans — highest priority locked entities.
 */
function findInsurerSpans(text: string): Span[] {
  const spans: Span[] = [];
  const lower = text.toLowerCase();
  // Sort longest first for greedy matching
  const sorted = [...INSURER_NAMES].sort((a, b) => b.length - a.length);
  for (const name of sorted) {
    let from = 0;
    while (from < lower.length) {
      const idx = lower.indexOf(name, from);
      if (idx === -1) break;
      // Word boundary check
      if (idx > 0 && /[a-zA-Z0-9]/.test(text[idx - 1])) {
        from = idx + 1;
        continue;
      }
      const end = idx + name.length;
      if (end < text.length && /[a-zA-Z0-9]/.test(text[end])) {
        from = idx + 1;
        continue;
      }
      spans.push({ start: idx, end });
      from = end;
    }
  }
  return mergeSpans(spans);
}

/**
 * Context-driven eponymous protection (Layer 1).
 * Finds positions where a capitalized word (or possessive/hyphenated form)
 * is flanked by clinical context indicators and locks them.
 * This runs BEFORE propagation so no tier can override it.
 */
function findEponymousContextSpans(text: string): Span[] {
  const spans: Span[] = [];
  // Match: capitalized words, possessive forms, and hyphenated pairs
  const capPattern = /(?:^|(?<=[\s(]))([A-Z][a-z]+(?:-[A-Z][a-z]+)*(?:'s?)?)/gm;
  let m: RegExpExecArray | null;
  while ((m = capPattern.exec(text)) !== null) {
    const token = m[1];
    const base = token.replace(/'s?$/, "").toLowerCase();

    // Check after-context
    const afterText = text
      .slice(m.index + token.length)
      .replace(/^['']?s?\s*/, "")
      .replace(/^[ \t]+/, "");
    const afterWord = afterText.match(/^([a-zA-Z]+)/);
    let afterClinical = false;
    if (afterWord && CLINICAL_CONTEXT_AFTER.has(afterWord[1].toLowerCase())) {
      afterClinical = true;
    }
    // Two-word after context: "not indicated"
    if (afterText.toLowerCase().startsWith("not indicated"))
      afterClinical = true;

    // Check before-context (same sentence only — don't cross periods)
    const beforeText = text.slice(Math.max(0, m.index - 20), m.index);
    // Truncate at last sentence boundary (period + space)
    const lastPeriod = beforeText.lastIndexOf(". ");
    const sameSentBefore =
      lastPeriod >= 0 ? beforeText.slice(lastPeriod + 2) : beforeText;
    const beforeWords = sameSentBefore.trim().split(/\s+/);
    const lastBefore = beforeWords[beforeWords.length - 1]
      ?.toLowerCase()
      .replace(/[.,;:]+$/, "");
    let beforeClinical = false;
    if (lastBefore && CLINICAL_CONTEXT_BEFORE.has(lastBefore)) {
      beforeClinical = true;
    }

    if (afterClinical || beforeClinical) {
      // Lock the full span including the 's if present
      let end = m.index + token.length;
      // Also check if the possessive 's follows immediately in original text
      const possAfter = text.slice(end).match(/^'s?\b/);
      if (possAfter) end += possAfter[0].length;
      spans.push({ start: m.index, end });
    }
  }
  return mergeSpans(spans);
}

/**
 * Find facility/organization pattern spans.
 * [Capitalized words] where the last word (or last two words) is an
 * organizational suffix → locked.
 */
function findFacilitySpans(text: string): Span[] {
  const spans: Span[] = [];
  // Match runs of 2+ capitalized words (same line only — use [ \t] not \s)
  const capRun = /(?:^|(?<=[\s(]))([A-Z][A-Za-z]*(?:[ \t]+[A-Z][A-Za-z]*)+)/gm;
  let m: RegExpExecArray | null;
  while ((m = capRun.exec(text)) !== null) {
    const words = m[1].split(/\s+/);
    if (words.length < 2) continue;

    // Check if last word is a facility suffix
    const lastWord = words[words.length - 1].toLowerCase();
    if (FACILITY_SUFFIXES.has(lastWord)) {
      spans.push({ start: m.index, end: m.index + m[0].length });
      continue;
    }

    // Check if last two words form a facility suffix
    if (words.length >= 3) {
      const lastTwo = (
        words[words.length - 2] +
        " " +
        words[words.length - 1]
      ).toLowerCase();
      if (FACILITY_SUFFIXES.has(lastTwo)) {
        spans.push({ start: m.index, end: m.index + m[0].length });
        continue;
      }
    }

    // Check if the word after the run is a facility suffix (lowercase)
    const afterRun = text.slice(m.index + m[0].length);
    const nextWord = afterRun.match(/^\s+([a-zA-Z]+)/);
    if (nextWord && FACILITY_SUFFIXES.has(nextWord[1].toLowerCase())) {
      const end = m.index + m[0].length + nextWord[0].length;
      if (end <= text.length) {
        spans.push({ start: m.index, end });
      }
    }
  }
  return mergeSpans(spans);
}

/**
 * Redact facility and practice names using the existing facility span detection.
 * Replaces detected facility names with [[FACILITY]] placeholder.
 * Skips insurer names (already handled separately) and already-redacted spans.
 */
function scrubFacilityNames(text: string): string {
  const facilitySpans = findFacilitySpans(text);
  const insurerSpans = findInsurerSpans(text);
  if (facilitySpans.length === 0) return text;

  // Filter out spans that overlap with insurer names
  const filtered = facilitySpans.filter((fs) => {
    for (const is_ of insurerSpans) {
      if (fs.start < is_.end && fs.end > is_.start) return false;
    }
    // Skip if already contains a placeholder
    const content = text.slice(fs.start, fs.end);
    if (content.includes("[[")) return false;
    return true;
  });

  // Replace from end to start to preserve indices
  let result = text;
  const sorted = [...filtered].sort((a, b) => b.start - a.start);
  for (const s of sorted) {
    result =
      result.slice(0, s.start) + FACILITY_PLACEHOLDER + result.slice(s.end);
  }
  return result;
}

function findProtectedLabelSpans(text: string): Span[] {
  const spans: Span[] = [];
  const lower = text.toLowerCase();
  for (const label of PROTECTED_LABELS) {
    let from = 0;
    while (from < lower.length) {
      const idx = lower.indexOf(label, from);
      if (idx === -1) break;
      if (idx > 0 && /[a-zA-Z0-9]/.test(text[idx - 1])) {
        from = idx + 1;
        continue;
      }
      const end = idx + label.length;
      if (end < text.length && /[a-zA-Z0-9]/.test(text[end])) {
        from = idx + 1;
        continue;
      }
      spans.push({ start: idx, end });
      from = end;
    }
  }
  return mergeSpans(spans);
}

function findSectionHeaderSpans(text: string): Span[] {
  const spans: Span[] = [];
  // Major section headers: ALL-CAPS + colon
  const re = /^[A-Z][A-Z\s\/]+:/gm;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    spans.push({ start: m.index, end: m.index + m[0].length });
  }
  // PE sub-section labels: "General:", "HEENT:", "Neuro:", etc.
  const subRe = /(?:^|(?<=\n))[ \t]*([A-Za-z][A-Za-z\s]*?):/gm;
  while ((m = subRe.exec(text)) !== null) {
    const label = m[1].trim().toLowerCase();
    if (PE_SUBLABELS.has(label)) {
      spans.push({ start: m.index, end: m.index + m[0].length });
    }
  }
  return spans;
}

// ============================================================================
// Name Region Parser
// ============================================================================

/**
 * Starting at `pos` in `text`, greedily consume everything that belongs to a
 * name. Returns the span or null if nothing valid was found.
 *
 * Consumes: initials, capitalized words (Mc/Mac/O'/D'/St./Al- prefixes),
 * nobiliary particles, hyphens, suffixes (Jr./Sr./II/III), comma+professional
 * suffixes (MD/DO), and possessives ('s).
 */
function parseNameRegion(
  text: string,
  pos: number,
  protectedSpans: Span[],
  hasPersonContext: boolean = false
): Span | null {
  let cursor = pos;
  let tokenCount = 0;
  let hasSurname = false;
  const MAX_TOKENS = 8;

  // Skip whitespace/separator between trigger and name
  while (cursor < text.length && /[\s,:;]/.test(text[cursor])) cursor++;
  const nameStart = cursor;

  // --- Helpers ---

  function tryInitial(): boolean {
    if (cursor >= text.length || !/[A-Z]/.test(text[cursor])) return false;
    if (cursor + 1 >= text.length || text[cursor + 1] !== ".") return false;
    // Must NOT be preceded by letter/digit (tail of abbreviation like PRN.)
    if (cursor > 0 && /[a-zA-Z0-9]/.test(text[cursor - 1])) return false;
    // Must NOT be a protected abbreviation
    // Check if this single letter + period is inside a larger uppercase token
    if (cursor > 0 && /[A-Z]/.test(text[cursor - 1])) return false;
    cursor += 2; // consume "X."
    tokenCount++;
    if (cursor < text.length && /[ \t]/.test(text[cursor])) cursor++; // skip space
    return true;
  }

  function tryCapWord(): string | null {
    if (cursor >= text.length) return null;

    // St. prefix: "St. James"
    if (
      text.slice(cursor, cursor + 3) === "St." &&
      cursor + 3 < text.length &&
      /[ \t]/.test(text[cursor + 3])
    ) {
      const after = cursor + 4;
      if (after < text.length && /[A-Z]/.test(text[after])) {
        const rest = text.slice(after).match(/^[A-Z][a-z]+/);
        if (rest) {
          cursor = after + rest[0].length;
          return "St. " + rest[0];
        }
      }
    }

    // O'/D' prefix: "O'Brien", "D'Angelo"
    if (
      /[OD]/.test(text[cursor]) &&
      text[cursor + 1] === "'" &&
      cursor + 2 < text.length &&
      /[A-Z]/.test(text[cursor + 2])
    ) {
      const rest = text
        .slice(cursor + 2)
        .match(/^[A-Z](?:(?:c|ac)[A-Z])?[a-z]+/);
      if (rest) {
        const w = text.slice(cursor, cursor + 2) + rest[0];
        cursor += 2 + rest[0].length;
        return w;
      }
    }

    // Al- prefix: "Al-Rashidi"
    if (
      text.slice(cursor, cursor + 3) === "Al-" &&
      cursor + 3 < text.length &&
      /[A-Z]/.test(text[cursor + 3])
    ) {
      const rest = text.slice(cursor + 3).match(/^[A-Z][a-z]+/);
      if (rest) {
        const w = "Al-" + rest[0];
        cursor += 3 + rest[0].length;
        return w;
      }
    }

    // Standard: Mc/Mac support
    const m = text.slice(cursor).match(/^[A-Z](?:(?:c|ac)[A-Z])?[a-z]+/);
    if (!m) return null;
    cursor += m[0].length;
    return m[0];
  }

  function tryHyphen(): boolean {
    if (cursor >= text.length || text[cursor] !== "-") return false;
    const saved = cursor;
    cursor++; // skip hyphen
    const w = tryCapWord();
    if (w) return true;
    cursor = saved;
    return false;
  }

  function tryParticle(): boolean {
    const remaining = text.slice(cursor);
    for (const p of PARTICLES) {
      // Case-insensitive particle matching: "Van der", "De la", "Von"
      const pLen = p.length;
      if (
        remaining.length >= pLen + 1 &&
        remaining.slice(0, pLen).toLowerCase() === p &&
        remaining[pLen] === " "
      ) {
        const after = cursor + pLen + 1;
        // Must be followed by a capitalized word (the surname)
        if (after < text.length && /[A-Z]/.test(text[after])) {
          cursor = after;
          tokenCount++;
          return true;
        }
      }
    }
    return false;
  }

  // --- Phase 1: Consume initials ---
  while (tokenCount < MAX_TOKENS && tryInitial()) {
    /* keep consuming */
  }

  // --- Phase 2: Consume name words ---
  let lastWasCap = false;
  while (tokenCount < MAX_TOKENS) {
    if (cursor >= text.length) break;
    if (text[cursor] === "\n") break;
    if (isInsideSectionHeader(text, cursor)) break;

    // Hard stop: double newline (paragraph boundary)
    if (
      cursor + 1 < text.length &&
      text[cursor] === "\n" &&
      text[cursor + 1] === "\n"
    )
      break;

    // Hard stop: period followed by newline — sentence boundary before paragraph/section
    if (
      cursor > nameStart &&
      cursor > 0 &&
      text[cursor - 1] === "." &&
      (text[cursor] === "\n" ||
        (text[cursor] === " " &&
          cursor + 1 < text.length &&
          text[cursor + 1] === "\n"))
    ) {
      cursor--;
      break;
    }

    // Hard stop: period + space + uppercase word that is NOT a name component
    // "Smith. The patient" — stop after "Smith", don't consume "The"
    if (
      cursor > nameStart &&
      hasSurname &&
      text[cursor] === "." &&
      cursor + 2 < text.length &&
      text[cursor + 1] === " " &&
      /[A-Z]/.test(text[cursor + 2])
    ) {
      // Check: is the next word a particle, prefix, or name continuation?
      const nextChunk = text.slice(cursor + 2, cursor + 20).toLowerCase();
      const isNameContinuation =
        PARTICLES.some((p) => nextChunk.startsWith(p + " ")) ||
        /^(?:mc|mac|st\.|al-|o'|d')[a-z]/i.test(
          text.slice(cursor + 2, cursor + 10)
        );
      if (!isNameContinuation) {
        // This period is a sentence terminator — stop before it
        break;
      }
    }

    // Space handling
    const savedPos = cursor;
    if (/[ \t]/.test(text[cursor])) cursor++;
    else if (lastWasCap && text[cursor] !== "-") break;

    // Try nobiliary particle FIRST (case-insensitive: "Van der", "De la", etc.)
    if (tryParticle()) {
      lastWasCap = false;
      continue;
    }

    // Check stop: lowercase word that isn't a particle
    if (cursor < text.length && /[a-z]/.test(text[cursor])) {
      cursor = savedPos;
      break;
    }

    // Check: protected abbreviation (case-sensitive for mixed-case, full-word match)
    const upToken = text.slice(cursor).match(/^[A-Z][A-Za-z0-9]+/);
    if (upToken && PROTECTED_ABBREVIATIONS.has(upToken[0])) {
      cursor = savedPos;
      break;
    }

    // Try capitalized word
    const word = tryCapWord();
    if (!word) {
      cursor = savedPos;
      break;
    }

    // Check: did we just consume a suffix (Jr, Sr)? If so, consume trailing "." and stop
    if (NAME_SUFFIXES.has(word.toLowerCase())) {
      if (cursor < text.length && text[cursor] === ".") cursor++;
      tokenCount++;
      break;
    }

    // Extend with hyphens
    while (tryHyphen()) {
      /* keep consuming */
    }

    tokenCount++;
    hasSurname = true;
    lastWasCap = true;
  }

  if (!hasSurname) return null;
  let nameEnd = cursor;

  // Hard boundary: if the name region ends right before a period + newline,
  // do NOT include the period. The period is a sentence terminator, not part
  // of the name. (Name suffixes like "Jr." are handled separately above.)
  if (nameEnd > nameStart && text[nameEnd - 1] === ".") {
    const afterPeriod = text.slice(nameEnd);
    if (/^[\s]*\n/.test(afterPeriod) || afterPeriod.length === 0) {
      // Check if the period is part of an initial (X.) or suffix (Jr.)
      const lastTwo = text.slice(Math.max(nameStart, nameEnd - 2), nameEnd);
      const isInitialOrSuffix =
        /^[A-Z]\.$/.test(lastTwo) ||
        NAME_SUFFIXES.has(
          text
            .slice(Math.max(nameStart, nameEnd - 3), nameEnd - 1)
            .trim()
            .toLowerCase()
        );
      if (!isInitialOrSuffix) {
        nameEnd--; // trim the sentence-ending period
      }
    }
  }

  // Validate: check consumed name words aren't ALL clinical whitelist terms
  // Skip this check when a title/role trigger provides person context
  if (!hasPersonContext) {
    const nameBody = text.slice(nameStart, nameEnd).trim();
    const nameWords = nameBody.split(/[\s-]+/).filter((w) => /^[A-Z]/.test(w));
    if (
      nameWords.length > 0 &&
      nameWords.every((w) => CLINICAL_WHITELIST.has(w.toLowerCase()))
    ) {
      return null;
    }
  }

  // --- Phase 3: Suffix (Jr., Sr., II, III, IV, V) ---
  const afterName = text.slice(nameEnd);
  const sfxM = afterName.match(/^[,\s]+([A-Za-z]+)\.?/);
  if (sfxM && NAME_SUFFIXES.has(sfxM[1].toLowerCase())) {
    nameEnd += sfxM[0].length;
  }

  // --- Phase 4: Professional suffix after comma (, MD  , DO) ---
  // NOTE: Professional suffixes are NOT consumed into the name span.
  // They remain visible in output: "[[NAME]], MD" not "[[NAME]]".
  // We just need to make sure we don't accidentally absorb the comma.

  // --- Phase 5: Do NOT consume possessive ('s) ---
  // Possessives are left in the text so output reads "[[NAME]]'s"

  if (nameEnd <= nameStart) return null;

  // Geographic check: single-word + comma + country/state = location
  const nameText = text.slice(nameStart, nameEnd);
  if (
    !/[\s]/.test(nameText.replace(/'s?$/, "").trim()) &&
    isGeoLocation(text, nameEnd)
  ) {
    return null;
  }

  return { start: nameStart, end: nameEnd };
}

// ============================================================================
// Pass 1 — Trigger-Based Detection
// ============================================================================

function triggerBasedDetection(
  text: string,
  protectedSpans: Span[],
  documentNames: TieredNames
): Span[] {
  const spans: Span[] = [];
  let m: RegExpExecArray | null;

  // Helper: invoke parser and register
  function tryParse(
    triggerStart: number,
    nameStart: number,
    absorbTrigger: boolean,
    personCtx: boolean = true,
    tier: number = TIER_STANDARD
  ): void {
    if (isProtectedPos(nameStart, protectedSpans)) return;
    if (isInsideSectionHeader(text, nameStart)) return;
    const region = parseNameRegion(text, nameStart, protectedSpans, personCtx);
    if (region) {
      // Post-check: region must not span across a blank line or double newline
      const regionText = text.slice(region.start, region.end);
      if (/\n\s*\n/.test(regionText)) {
        const blankIdx = regionText.search(/\n\s*\n/);
        if (blankIdx > 0) {
          region.end = region.start + blankIdx;
        } else return;
      }

      // Post-match clinical context check: if the word immediately AFTER
      // the matched name region is a clinical context indicator, reject
      // the match — this is an eponymous term, not a person.
      // e.g. "Grey Turner" + "sign" → reject. "Sister Mary Joseph" + "node" → reject.
      const afterRegion = text
        .slice(region.end)
        .replace(/^['']?s?\s*/, "")
        .replace(/^[ \t]+/, "");
      const afterWord = afterRegion.match(/^([a-zA-Z]+)/);
      if (afterWord && CLINICAL_CONTEXT_AFTER.has(afterWord[1].toLowerCase()))
        return;
      // Also check "not indicated" two-word pattern
      if (afterRegion.toLowerCase().startsWith("not indicated")) return;

      // Pre-match clinical context check: if the word immediately BEFORE
      // the name region start is a clinical context indicator, reject.
      // e.g. "positive" + "Rovsing" (when parsed via NER or other trigger).
      const beforeRegion = text.slice(
        Math.max(0, region.start - 20),
        region.start
      );
      const beforeWords = beforeRegion.trim().split(/\s+/);
      const lastBefore = beforeWords[beforeWords.length - 1]
        ?.toLowerCase()
        .replace(/[.,;:]+$/, "");
      if (lastBefore && CLINICAL_CONTEXT_BEFORE.has(lastBefore)) return;

      const span = absorbTrigger
        ? { start: triggerStart, end: region.end }
        : region;
      spans.push(span);
      registerNameComponents(
        text,
        region.start,
        region.end,
        documentNames,
        tier
      );
    }
  }

  // --- Title triggers ---
  // Pre-compute compound descriptor tails (e.g. "charge" + "nurse")
  const compoundTails = new Map<string, string[]>();
  for (const desc of ROLE_DESCRIPTORS) {
    const parts = desc.split(/\s+/);
    if (parts.length >= 2) {
      const tail = parts[parts.length - 1].toLowerCase();
      if (TITLE_SET.has(tail)) {
        if (!compoundTails.has(tail)) compoundTails.set(tail, []);
        compoundTails
          .get(tail)!
          .push(parts.slice(0, -1).join(" ").toLowerCase());
      }
    }
  }

  const titleAlt = TITLES.map(escapeRegex).join("|");
  const titleRe = new RegExp(
    `(?:^|(?<=[\\s(]))((?:${titleAlt})\\.?)[ \\t]+`,
    "gm"
  );
  while ((m = titleRe.exec(text)) !== null) {
    if (isProtectedPos(m.index, protectedSpans)) continue;

    // Check: is this title the tail of a compound role descriptor?
    const matchedTitle = m[1].replace(/\.$/, "").toLowerCase();
    const prefixes = compoundTails.get(matchedTitle);
    if (prefixes) {
      const before = text
        .slice(Math.max(0, m.index - 40), m.index)
        .toLowerCase()
        .trimEnd();
      if (prefixes.some((p) => before.endsWith(p))) continue; // skip — it's a compound
    }

    tryParse(m.index, m.index + m[0].length, true);
  }

  // --- Role descriptor triggers ---
  const descAlt = ROLE_DESCRIPTORS_SORTED.map(escapeRegex).join("|");
  const descRe = new RegExp(
    `(?:^|(?<=[\\s,;(]))(${descAlt})[,:]?[ \\t]+`,
    "gim"
  );
  while ((m = descRe.exec(text)) !== null) {
    if (isProtectedPos(m.index, protectedSpans)) continue;
    if (isInsideSectionHeader(text, m.index)) continue;
    tryParse(m.index, m.index + m[0].length, false, false);
  }

  // --- Certified wildcard pattern ---
  // "certified [1-3 words] [base role]" where base role is a known noun
  const baseRoles =
    "nurse|technologist|educator|specialist|practitioner|counselor|consultant|therapist|coordinator|sonographer|assistant|midwife";
  const certWild = new RegExp(
    `(?:^|(?<=[\\s(]))(?:certified|licensed)\\s+(?:\\w+\\s+){0,3}(?:${baseRoles})[,:]?[ \\t]+`,
    "gim"
  );
  while ((m = certWild.exec(text)) !== null) {
    if (isProtectedPos(m.index, protectedSpans)) continue;
    if (isInsideSectionHeader(text, m.index)) continue;
    tryParse(m.index, m.index + m[0].length, false, false);
  }

  // --- Label + colon triggers ---
  const labelAlt = NAME_LABELS.map(escapeRegex).join("|");
  const labelRe = new RegExp(`(${labelAlt})\\s*:\\s*`, "gi");
  while ((m = labelRe.exec(text)) !== null) {
    // Patient: label → Tier 1 (patient-origin)
    const labelWord = m[1].toLowerCase();
    const tier =
      labelWord === "patient" || labelWord === "pt"
        ? TIER_PATIENT
        : TIER_STANDARD;
    tryParse(m.index, m.index + m[0].length, false, true, tier);
  }

  // --- "Patient" without colon (e.g. "Patient Kwame Mensah-Osei Jr.") ---
  const patientRe = /(?:^|(?<=[\s(]))Patient[ \t]+(?=[A-Z])/gim;
  while ((m = patientRe.exec(text)) !== null) {
    if (isProtectedPos(m.index, protectedSpans)) continue;
    tryParse(m.index, m.index + m[0].length, false, true, TIER_PATIENT);
  }

  // --- Action-by triggers ---
  const actionAlt = ACTION_BY_LABELS.map(escapeRegex).join("|");
  const actionRe = new RegExp(`(?:${actionAlt})[ \\t]+`, "gim");
  while ((m = actionRe.exec(text)) !== null) {
    if (isProtectedPos(m.index, protectedSpans)) continue;
    tryParse(m.index, m.index + m[0].length, false);
  }

  // --- Relationship triggers ---
  const relAlt = RELATIONSHIP_DESCRIPTORS.map(escapeRegex).join("|");
  const relRe = new RegExp(
    `(?:his|her|their|patient'?s?|the\\s+patient'?s?)\\s+(?:${relAlt})[,:]?[ \\t]+`,
    "gim"
  );
  while ((m = relRe.exec(text)) !== null) {
    tryParse(m.index, m.index + m[0].length, false, true, TIER_RELATIONSHIP);
  }

  // --- NER supplementary ---
  const nerSpans = runNerDetection(text, protectedSpans);
  for (const s of nerSpans) {
    spans.push(s);
    registerNameComponents(text, s.start, s.end, documentNames, TIER_STANDARD);
  }

  // --- Standalone initials triggers (no preceding trigger needed) ---
  const capWordI = `(?:[DOd]')?[A-Z](?:(?:c|ac)[A-Z])?[a-z]+(?:-(?:[DOd]')?[A-Z](?:(?:c|ac)[A-Z])?[a-z]+)*`;
  // Double: M.T. Johnson, P.J. McMurray
  const dblRe = new RegExp(
    `(?:^|(?<=\\s))[A-Z]\\.\\s?[A-Z]\\.\\s?${capWordI}`,
    "g"
  );
  while ((m = dblRe.exec(text)) !== null) {
    if (isProtectedPos(m.index, protectedSpans)) continue;
    if (isInsideSectionHeader(text, m.index)) continue;
    spans.push({ start: m.index, end: m.index + m[0].length });
    registerNameComponents(
      text,
      m.index,
      m.index + m[0].length,
      documentNames,
      TIER_STANDARD
    );
  }
  // Single: A. Patel, J. Smith
  const sglRe = new RegExp(`(?:^|(?<=\\s))[A-Z]\\.\\s?${capWordI}`, "g");
  while ((m = sglRe.exec(text)) !== null) {
    if (isProtectedPos(m.index, protectedSpans)) continue;
    if (isInsideSectionHeader(text, m.index)) continue;
    spans.push({ start: m.index, end: m.index + m[0].length });
    registerNameComponents(
      text,
      m.index,
      m.index + m[0].length,
      documentNames,
      TIER_STANDARD
    );
  }

  // --- Last, First EHR format ---
  const lfSpans = detectLastCommaFirst(text);
  for (const s of lfSpans) {
    spans.push(s);
    registerNameComponents(text, s.start, s.end, documentNames, TIER_STANDARD);
  }

  return spans;
}

function registerNameComponents(
  text: string,
  start: number,
  end: number,
  names: TieredNames,
  tier: number
): void {
  let n = text.slice(start, end).trim();
  n = n.replace(/'s?$/, "");
  n = n.replace(
    /,\s*(?:MD|DO|ND|PhD|DDS|DMD|DPM|OD|PharmD|PsyD|DNP|MSN|MPH)\.?\s*$/i,
    ""
  );
  n = n.replace(/[\s,]+(?:Jr|Sr|II|III|IV|V|Esq)\.?\s*$/i, "");
  for (const t of TITLES) {
    n = n.replace(new RegExp(`^${escapeRegex(t)}\\.?\\s+`, "i"), "");
  }
  n = n.trim();
  if (n.length < 2) return;

  // Helper: add name at tier, keeping highest confidence (lowest number)
  function addName(name: string, t: number): void {
    const existing = names.get(name);
    if (existing === undefined || t < existing) names.set(name, t);
  }

  addName(n, tier);
  for (const w of n.split(/[\s]+/)) {
    if (w.length > 1 && (/^[A-Z]/.test(w) || /^[OD]'/.test(w))) {
      addName(w, tier);
      if (w.includes("-"))
        for (const p of w.split("-"))
          if (p.length > 1 && /^[A-Z]/.test(p)) addName(p, tier);
    }
  }
}

// ============================================================================
// NER Supplementary
// ============================================================================

function runNerDetection(text: string, protectedSpans: Span[]): Span[] {
  const doc = nlp(text);
  const people = doc.people();
  const spans: Span[] = [];
  const seen = new Set<string>();

  // Pre-compute relationship words to strip from NER terms
  const relWords = new Set(
    RELATIONSHIP_DESCRIPTORS.filter((r) => !r.includes(" ")).map((r) =>
      r.toLowerCase()
    )
  );

  people.forEach((person: any) => {
    let term = person.text();
    if (!term || term.trim().length === 0) return;

    // Strip labels
    const tl = term.toLowerCase();
    for (const label of PROTECTED_LABELS) {
      if (tl.startsWith(label)) {
        const r = term.slice(label.length).replace(/^[\s,:]+/, "");
        if (r.length > 0 && /^[A-Z]/.test(r)) {
          term = r;
          break;
        }
      }
    }

    // Strip relationship words NER absorbs: "sister Faith" → "Faith"
    const termWords = term.split(/\s+/);
    if (termWords.length >= 2 && relWords.has(termWords[0].toLowerCase())) {
      term = termWords.slice(1).join(" ");
    }

    // Strip possessives, trailing period, suffixes
    term = term.replace(/'s?\s*$/, "").trim();
    if (
      term.endsWith(".") &&
      !/^[A-Z]\.$/.test(term) &&
      !/\.\s?[A-Z]\.$/.test(term)
    )
      term = term.slice(0, -1).trim();
    term = term.replace(/[\s,]+(?:Jr|Sr|II|III|IV|V|Esq)\.?$/i, "").trim();

    const words = term.split(/\s+/);
    if (words.length === 1) {
      const low = words[0].toLowerCase();
      if (CLINICAL_WHITELIST.has(low)) return;
      if (COMMON_WORDS.has(low)) return;
      if (
        low.includes("-") &&
        low.split("-").every((p) => CLINICAL_WHITELIST.has(p))
      )
        return;
      if (/^[A-Z]{2,6}\.?$/.test(words[0])) return;
      if (PROTECTED_ABBREVIATIONS.has(words[0])) return;
    }

    // Skip entire NER terms that look like facility patterns
    // e.g. "General ED", "ED triage"
    if (words.some((w) => PROTECTED_ABBREVIATIONS.has(w))) return;
    if (words.length >= 2) {
      const lastLow = words[words.length - 1].toLowerCase();
      if (FACILITY_SUFFIXES.has(lastLow)) return;
    }

    if (seen.has(term)) return;
    seen.add(term);

    const esc = escapeRegex(term);
    const hasApo = term.includes("'");
    const bb = hasApo ? `(?:^|(?<=[\\s,;:(]))` : `\\b`;
    const ba = hasApo ? `(?=[\\s.,;:!?)\\]]|$)` : `\\b`;
    const re = new RegExp(`${bb}${esc}${ba}`, "g");

    let match: RegExpExecArray | null;
    while ((match = re.exec(text)) !== null) {
      let sp = { start: match.index, end: match.index + match[0].length };
      if (isInsideSectionHeader(text, sp.start)) continue;
      if (isProtectedPos(sp.start, protectedSpans)) continue;

      // Expand hyphenated names: "Osei" → "Mensah-Osei"
      const fwd = text.slice(sp.end).match(/^(-[A-Z][a-zA-Z]+)+/);
      if (fwd) sp = { start: sp.start, end: sp.end + fwd[0].length };
      const bwd = text
        .slice(Math.max(0, sp.start - 40), sp.start)
        .match(/([A-Z][a-zA-Z]+-)+$/);
      if (bwd) sp = { start: sp.start - bwd[0].length, end: sp.end };

      // Also expand backward into preceding capitalized first name
      // "Kwame Mensah-Osei" — expand "Mensah-Osei" to include "Kwame"
      // But NOT into role descriptors/titles: "Psychiatrist Erica Stone" should not absorb "Psychiatrist"
      const beforeStart = text.slice(Math.max(0, sp.start - 50), sp.start);
      const prevCapWords = beforeStart.match(/((?:[A-Z][a-z]+ )+)$/);
      if (prevCapWords) {
        const words = prevCapWords[1].trim().split(/\s+/);
        const roleDescSet = new Set(
          ROLE_DESCRIPTORS.map((r) => r.toLowerCase())
        );
        const labelWords = new Set(
          NAME_LABELS.map((l) => l.split(/\s+/)[0].toLowerCase())
        );
        const stopLower = new Set([
          "per",
          "the",
          "a",
          "an",
          "at",
          "in",
          "on",
          "for",
          "from",
          "to",
          "with",
          "under",
          "via",
          "who",
          "that",
          "which",
          "and",
          "or",
          "but",
          "by",
          "of",
          "as",
          "so",
          "if",
          "no",
          "not",
        ]);
        const allNames = words.every((w) => {
          const low = w.toLowerCase();
          return (
            /^[A-Z][a-z]+$/.test(w) &&
            !CLINICAL_WHITELIST.has(low) &&
            !TITLE_SET.has(low) &&
            !roleDescSet.has(low) &&
            !labelWords.has(low) &&
            !stopLower.has(low)
          );
        });
        if (allNames) {
          sp = { start: sp.start - prevCapWords[0].length, end: sp.end };
        }
      }

      // Post-match clinical context check: if the word immediately after
      // the span is a clinical context indicator, this is an eponymous term.
      const afterNer = text
        .slice(sp.end)
        .replace(/^['']?s?\s*/, "")
        .replace(/^[ \t]+/, "");
      const afterNerWord = afterNer.match(/^([a-zA-Z]+)/);
      if (
        afterNerWord &&
        CLINICAL_CONTEXT_AFTER.has(afterNerWord[1].toLowerCase())
      )
        continue;

      // Also check preceded-by (same sentence)
      const beforeNer = text.slice(Math.max(0, sp.start - 20), sp.start);
      const lastPNer = beforeNer.lastIndexOf(". ");
      const sameSentNer =
        lastPNer >= 0 ? beforeNer.slice(lastPNer + 2) : beforeNer;
      const bwNer = sameSentNer.trim().split(/\s+/);
      const lbNer = bwNer[bwNer.length - 1]
        ?.toLowerCase()
        .replace(/[.,;:]+$/, "");
      if (lbNer && CLINICAL_CONTEXT_BEFORE.has(lbNer)) continue;

      spans.push(sp);
    }
  });
  return spans;
}

// ============================================================================
// Last, First EHR Format
// ============================================================================

function detectLastCommaFirst(text: string): Span[] {
  const spans: Span[] = [];
  const capW = `(?:[DOd]')?[A-Z](?:(?:c|ac)[A-Z])?[a-z]+`;
  const re = new RegExp(`${capW},\\s?${capW}(?:\\s[A-Z]\\.?)?`, "g");
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const parts = m[0].split(",").map((p) => p.trim());
    const last = parts[0].toLowerCase().replace(/'/g, "");
    const first =
      parts[1]?.split(/\s/)[0]?.toLowerCase().replace(/'/g, "") ?? "";
    if (CLINICAL_WHITELIST.has(last) && CLINICAL_WHITELIST.has(first)) continue;
    if (
      COUNTRIES.has(first) ||
      COUNTRIES.has(first.split(/\s+/)[0]) ||
      US_STATES.has(first) ||
      (first.length <= 2 && US_STATES.has(first))
    )
      continue;
    if (isInsideSectionHeader(text, m.index)) continue;
    spans.push({ start: m.index, end: m.index + m[0].length });
  }
  return spans;
}

/**
 * Check if a span is part of a facility name pattern.
 * "Mercy General ED", "Mercy Hematology", "Noble Foundation"
 * Pattern: [Name] followed by facility suffix term, or preceded by preposition
 * and followed by facility-like capitalized words.
 */
function isFacilityContext(text: string, span: Span): boolean {
  const afterText = text.slice(span.end).replace(/^[ \t]+/, "");
  const afterWord = afterText.match(/^([A-Za-z]+)/);
  if (afterWord) {
    const aw = afterWord[1].toLowerCase();
    if (FACILITY_SUFFIXES.has(aw)) return true;
    // Also check two-word facility suffixes: "Medical Center", "Health Center"
    const afterTwoWords = afterText.match(/^([A-Za-z]+)\s+([A-Za-z]+)/);
    if (afterTwoWords) {
      const twoWord = (afterTwoWords[1] + " " + afterTwoWords[2]).toLowerCase();
      if (FACILITY_SUFFIXES.has(twoWord)) return true;
    }
  }

  // Check: is preceded by a preposition and followed by more capitalized words
  // that end with a facility suffix? "at Mercy General ED"
  const before = text
    .slice(Math.max(0, span.start - 10), span.start)
    .trim()
    .split(/\s+/);
  const lastBefore = before[before.length - 1]?.toLowerCase();
  if (lastBefore && LOCATION_PREPOSITIONS.has(lastBefore)) {
    // Walk forward to see if this is a multi-word facility name
    const ahead = text.slice(span.end);
    const capWords = ahead.match(/^(?:[ \t]+[A-Z][a-zA-Z]*)+/);
    if (capWords) {
      const allWords = capWords[0].trim().split(/\s+/);
      if (allWords.some((w) => FACILITY_SUFFIXES.has(w.toLowerCase())))
        return true;
      if (allWords.some((w) => PROTECTED_ABBREVIATIONS.has(w))) return true;
    }
  }

  return false;
}

// ============================================================================
// Pass 2 — Known Names Propagation (Three-Tier)
// ============================================================================

function propagateKnownNames(
  text: string,
  names: TieredNames,
  protectedSpans: Span[]
): Span[] {
  const spans: Span[] = [];

  // Pre-compute multi-word city positions
  const citySpans: Span[] = [];
  const lowerText = text.toLowerCase();
  for (const city of MULTI_WORD_CITIES) {
    let from = 0;
    while (from < lowerText.length) {
      const idx = lowerText.indexOf(city, from);
      if (idx === -1) break;
      if (idx > 0 && /[a-zA-Z]/.test(text[idx - 1])) {
        from = idx + 1;
        continue;
      }
      const end = idx + city.length;
      if (end < text.length && /[a-zA-Z]/.test(text[end])) {
        from = idx + 1;
        continue;
      }
      citySpans.push({ start: idx, end });
      from = end;
    }
  }

  /**
   * Check if a matched span is part of a multi-word eponymous sequence.
   * "Grey" at start of "Grey Turner sign" → true (CapWord + clinical indicator ahead).
   * Walks forward from span.end through capitalized words and hyphens,
   * checking if a clinical context indicator follows the sequence.
   */
  function isPartOfEponymousSequence(sp: Span): boolean {
    let cursor = sp.end;
    // Walk forward through cap words / hyphens / possessives
    let steps = 0;
    while (steps < 4) {
      // Skip whitespace
      const ws = text.slice(cursor).match(/^[ \t]+/);
      if (ws) cursor += ws[0].length;
      // Skip possessive 's
      const poss = text.slice(cursor).match(/^'s?\b/);
      if (poss) cursor += poss[0].length;
      // Try to match another capitalized word or hyphenated pair
      const capWord = text.slice(cursor).match(/^[A-Z][a-z]+(?:-[A-Z][a-z]+)*/);
      if (!capWord) break;
      cursor += capWord[0].length;
      steps++;
      // After consuming, check if the NEXT word is a clinical indicator
      const afterCap = text
        .slice(cursor)
        .replace(/^['']?s?\s*/, "")
        .replace(/^[ \t]+/, "");
      const nextW = afterCap.match(/^([a-zA-Z]+)/);
      if (nextW && CLINICAL_CONTEXT_AFTER.has(nextW[1].toLowerCase()))
        return true;
      if (afterCap.toLowerCase().startsWith("not indicated")) return true;
    }
    // Also check: does the immediate after-text of the span (after cap words) hit a clinical term?
    // This handles the direct case where the span is the LAST word before the indicator
    const directAfter = text
      .slice(sp.end)
      .replace(/^['']?s?\s*/, "")
      .replace(/^[ \t]+/, "");
    const directWord = directAfter.match(/^([a-zA-Z]+)/);
    if (directWord && CLINICAL_CONTEXT_AFTER.has(directWord[1].toLowerCase()))
      return true;
    return false;
  }

  for (const [name, tier] of names) {
    if (name.length < 2) continue;

    const isCommon =
      name.split(/[\s-]+/).length === 1 && COMMON_WORDS.has(name.toLowerCase());

    const esc = escapeRegex(name);
    const hasApo = name.includes("'");
    const bb = hasApo ? `(?:^|(?<=[\\s,;:(]))` : `\\b`;
    const ba = hasApo ? `(?=[\\s.,;:!?)\\]'"]|$)` : `\\b`;
    const re = new RegExp(`${bb}${esc}${ba}`, "g");
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const sp = { start: m.index, end: m.index + m[0].length };
      if (isInsideSectionHeader(text, sp.start)) continue;
      if (isProtectedPos(sp.start, protectedSpans)) continue;
      if (/\n/.test(text.slice(sp.start, sp.end))) continue;

      // City protection
      let inCity = false;
      for (const c of citySpans) {
        if (sp.start >= c.start && sp.end <= c.end) {
          inCity = true;
          break;
        }
      }
      if (inCity) continue;

      // Facility pattern protection (all tiers)
      if (isCommon && isFacilityContext(text, sp)) continue;

      // Multi-word eponymous sequence check (ALL tiers, including Tier 1).
      // If this token is part of a sequence like "Grey Turner sign" where
      // capitalized words lead into a clinical context indicator, preserve it.
      // This applies even to patient-origin names — "Grey Turner sign" is
      // a clinical term regardless of whether Grey is the patient's name.
      if (isPartOfEponymousSequence(sp)) continue;

      // Also check: preceded by a clinical context indicator (same-sentence)
      const beforeProp = text.slice(Math.max(0, sp.start - 20), sp.start);
      const lastPeriod = beforeProp.lastIndexOf(". ");
      const sameSentBefore =
        lastPeriod >= 0 ? beforeProp.slice(lastPeriod + 2) : beforeProp;
      const bwProp = sameSentBefore.trim().split(/\s+/);
      const lbProp = bwProp[bwProp.length - 1]
        ?.toLowerCase()
        .replace(/[.,;:]+$/, "");
      if (lbProp && CLINICAL_CONTEXT_BEFORE.has(lbProp)) continue;

      // Three-tier confidence check for common words
      if (isCommon) {
        if (tier === TIER_PATIENT) {
          // Tier 1: propagate freely — patient's own name. No checks needed.
          spans.push(sp);
          continue;
        } else if (tier === TIER_RELATIONSHIP) {
          // Tier 2: light check — skip facility/clinical contexts only
          const afterText = text.slice(sp.end).replace(/^[ \t]+/, "");
          const nextWord = afterText.match(/^([A-Za-z]+)/);
          if (nextWord) {
            const nw = nextWord[1].toLowerCase();
            if (
              FACILITY_SUFFIXES.has(nw) ||
              PROTECTED_ABBREVIATIONS.has(nextWord[1])
            )
              continue;
            if (/^[A-Za-z]+:/.test(afterText)) continue; // sub-label
          }
          // Tier 2 passes: skip eponymous check — confirmed person name
          spans.push(sp);
          continue;
        } else {
          // Tier 3: full context check
          if (!hasPersonContext(text, sp, names)) continue;
        }
      }

      // Eponymous check (only reaches here for Tier 3 or non-common names)
      const low = text.slice(sp.start, sp.end).toLowerCase().replace(/'/g, "");
      if (CLINICAL_WHITELIST.has(low) && !resolveEponymous(text, sp, names))
        continue;

      // Geo check
      if (isGeoLocation(text, sp.end)) {
        const bw = text
          .slice(Math.max(0, sp.start - 15), sp.start)
          .trim()
          .split(/\s+/);
        if (LOCATION_PREPOSITIONS.has(bw[bw.length - 1]?.toLowerCase() || ""))
          continue;
      }

      spans.push(sp);
    }
  }
  return spans;
}

function resolveEponymous(
  text: string,
  span: Span,
  names: TieredNames
): boolean {
  const afterText = text.slice(span.end).replace(/^[ \t]+/, "");
  const aw = afterText.match(/^([a-zA-Z]+)/);
  if (aw) {
    if (CLINICAL_CONTEXT_AFTER.has(aw[1].toLowerCase())) return false;
    if (PERSON_CONTEXT_AFTER.has(aw[1].toLowerCase())) return true;
  }
  const beforeText = text.slice(Math.max(0, span.start - 80), span.start);
  // Same-sentence only: truncate at last period + space
  const lastP = beforeText.lastIndexOf(". ");
  const sameSent = lastP >= 0 ? beforeText.slice(lastP + 2) : beforeText;
  const bw = sameSent.trim().split(/\s+/);
  const lb = bw[bw.length - 1]?.replace(/[.,;:]+$/, "") || "";
  if (CLINICAL_CONTEXT_BEFORE.has(lb.toLowerCase())) return false;
  if (TITLE_SET.has(lb.toLowerCase().replace(/\.$/, ""))) return true;
  if (sameSent.trimEnd().endsWith(NAME_PLACEHOLDER)) return true;
  if (names.has(lb)) return true;
  return false;
}

// ============================================================================
// Pass 3 — [[NAME]] Adjacency Cleanup
// ============================================================================

function cleanupAdjacency(text: string): string {
  const capW = `(?:[DOd]')?[A-Z](?:(?:c|ac)[A-Z])?[a-z]+(?:-(?:[DOd]')?[A-Z](?:(?:c|ac)[A-Z])?[a-z]+)*`;
  const verbs = [
    "documented",
    "assisted",
    "reviewed",
    "coordinated",
    "created",
    "provided",
    "obtained",
    "administered",
    "ordered",
    "called",
    "evaluated",
    "examined",
    "assessed",
    "noted",
    "reported",
    "completed",
    "performed",
    "recommended",
    "discussed",
    "confirmed",
    "contacted",
    "notified",
    "signed",
    "approved",
    "verified",
    "prepared",
    "started",
    "stopped",
    "continued",
    "returned",
    "presented",
    "denied",
    "complained",
    "stated",
    "mentioned",
    "indicated",
    "requested",
    "from",
    "at",
    "to",
    "is",
    "was",
    "will",
    "has",
    "had",
    "the",
    "a",
    "an",
    "on",
    "in",
    "for",
    "with",
    "and",
    "or",
    "but",
    "who",
  ].join("|");
  const re = new RegExp(
    `(\\[\\[NAME\\]\\])[ \\t]+(${capW})(?:'s?)?(?=[ \\t]+(?:${verbs})\\b|[.,;:!?)\\]])`,
    "g"
  );
  let result = text.replace(re, "$1");
  result = result.replace(/\[\[NAME\]\][ \t]*\[\[NAME\]\]/g, NAME_PLACEHOLDER);
  return result;
}

// ============================================================================
// Main Pipeline
// ============================================================================

export function scrubNames(text: string): string {
  if (!text || text.trim().length === 0) return text;

  // ===== GLOBAL PRECEDENCE CHAIN =====
  // Step 1: Lock protected entities (highest priority)
  const labelSpans = findProtectedLabelSpans(text);
  const headerSpans = findSectionHeaderSpans(text);
  const insurerSpans = findInsurerSpans(text);

  // Step 2: Lock eponymous context spans (Layer 1)
  const eponymousSpans = findEponymousContextSpans(text);

  // Step 3: Lock facility/organization pattern spans
  const facilitySpans = findFacilitySpans(text);

  // Step 4: Lock geographic locations (handled in propagation, but also lock multi-word cities)
  // (Geographic single-word + comma patterns are handled dynamically)

  // Combine ALL locked spans — these cannot be overridden by any propagation tier
  const allProtected = mergeSpans([
    ...labelSpans,
    ...headerSpans,
    ...insurerSpans,
    ...eponymousSpans,
    ...facilitySpans,
  ]);

  // Steps 5-7: Trigger detection + tiered propagation
  const documentNames: TieredNames = new Map();
  const triggerSpans = triggerBasedDetection(text, allProtected, documentNames);
  const propSpans = propagateKnownNames(text, documentNames, allProtected);

  // Combine, filter against ALL protected spans, expand, merge
  const all = [...triggerSpans, ...propSpans];
  const filtered: Span[] = [];
  for (const s of all) {
    let span: Span | null = { ...s };
    for (const p of allProtected) {
      if (!span) break;
      if (span.start >= p.start && span.end <= p.end) {
        span = null;
        break;
      }
      if (span.start >= p.start && span.start < p.end && span.end > p.end) {
        let newStart = p.end;
        while (newStart < span.end && /[\s,:;]/.test(text[newStart]))
          newStart++;
        if (newStart < span.end) {
          span = { start: newStart, end: span.end };
        } else {
          span = null;
        }
      }
      if (span && span.start < p.start && span.end > p.end) {
        let newStart = p.end;
        while (newStart < span.end && /[\s,:;]/.test(text[newStart]))
          newStart++;
        if (newStart < span.end) {
          span = { start: newStart, end: span.end };
        } else {
          span = null;
        }
      }
    }
    if (span) filtered.push(span);
  }

  // Post-processing: truncate any span that crosses a blank line (Fix 3)
  const truncated = filtered
    .map((s) => {
      const regionText = text.slice(s.start, s.end);
      const blankIdx = regionText.search(/\n\s*\n/);
      if (blankIdx > 0) return { start: s.start, end: s.start + blankIdx };
      return s;
    })
    .filter((s) => s.end > s.start);

  // Expand trailing suffixes
  const expanded = truncated.map((s) => {
    let end = s.end;
    const sfxRe = text.slice(end).match(/^[\s,]+([A-Za-z]+)\.?/);
    if (sfxRe && NAME_SUFFIXES.has(sfxRe[1].toLowerCase())) {
      end += sfxRe[0].length;
    }
    return { start: s.start, end };
  });
  const merged = mergeSpans(expanded);

  let result = replaceSpans(text, merged);
  result = cleanupAdjacency(result);
  return result;
}

// ============================================================================
// DOB Redaction — runs BEFORE name detection
// ============================================================================

const DOB_PLACEHOLDER = "[[DOB]]";

/**
 * Redact explicitly labeled dates of birth. Only matches dates that follow
 * a recognized DOB label. Unlabeled dates are never touched.
 *
 * Runs before name detection so date tokens (e.g. "May") don't interact
 * with the Name Region Parser.
 */
function scrubDOB(text: string): string {
  // Label patterns (case-insensitive)
  const labels = [
    "Date of Birth",
    "Date of birth",
    "date of birth",
    "Birth Date",
    "Birth date",
    "birth date",
    "Birthdate",
    "birthdate",
    "D\\.O\\.B\\.",
    "D\\.O\\.B",
    "DOB",
  ];
  const labelAlt = labels.join("|");

  // Month names
  const monthFull =
    "January|February|March|April|May|June|July|August|September|October|November|December";
  const monthAbbr = "Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec";
  const monthAny = `(?:${monthFull}|${monthAbbr})`;

  // Date format patterns (after label + separator)
  const datePatterns = [
    // MM/DD/YYYY, M/D/YYYY (slash)
    `\\d{1,2}/\\d{1,2}/\\d{4}`,
    // MM-DD-YYYY, M-D-YYYY (dash)
    `\\d{1,2}-\\d{1,2}-\\d{4}`,
    // MM.DD.YYYY, M.D.YYYY (dot)
    `\\d{1,2}\\.\\d{1,2}\\.\\d{4}`,
    // YYYY-MM-DD (ISO)
    `\\d{4}-\\d{2}-\\d{2}`,
    // Month DD, YYYY or Month DD YYYY
    `${monthAny}\\.?\\s+\\d{1,2},?\\s+\\d{4}`,
    // DD Month YYYY
    `\\d{1,2}\\s+${monthAny}\\.?\\s+\\d{4}`,
  ];
  const dateAlt = datePatterns.join("|");

  // Combined pattern: label + separator + date
  // Separator: colon + optional space, or just space
  const pattern = new RegExp(`((?:${labelAlt})[:\\s]\\s*)(${dateAlt})`, "gi");

  return text.replace(pattern, `$1${DOB_PLACEHOLDER}`);
}

// ============================================================================
// Service Date Redaction — runs BEFORE name detection
// ============================================================================

const SERVICE_DATE_PLACEHOLDER = "[[DATE]]";

/**
 * Extract the Date of Service (or first labeled service date) from the text
 * as a reference point for narrative date offsetting.
 * Returns null if no labeled service date is found.
 */
function extractServiceDate(text: string): Date | null {
  const monthMap: Record<string, number> = {
    january: 0,
    february: 1,
    march: 2,
    april: 3,
    may: 4,
    june: 5,
    july: 6,
    august: 7,
    september: 8,
    october: 9,
    november: 10,
    december: 11,
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
  };

  // Look for "Date of Service" specifically first, then fall back to other labels
  const dosLabels = [
    "Date of Service",
    "Date of Encounter",
    "Date of Visit",
    "Service Date",
    "Encounter Date",
    "Visit Date",
  ];

  const monthFull =
    "January|February|March|April|May|June|July|August|September|October|November|December";
  const monthAbbr = "Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec";
  const monthAny = `(?:${monthFull}|${monthAbbr})`;

  const datePatterns = [
    `(\\d{1,2})/(\\d{1,2})/(\\d{4})`,
    `(\\d{1,2})-(\\d{1,2})-(\\d{4})`,
    `(\\d{4})-(\\d{2})-(\\d{2})`,
    `(${monthAny})\\.?\\s+(\\d{1,2}),?\\s+(\\d{4})`,
  ];

  for (const label of dosLabels) {
    for (const dp of datePatterns) {
      const re = new RegExp(`${escapeRegex(label)}[:\\s]\\s*${dp}`, "i");
      const m = text.match(re);
      if (m) {
        // Parse based on which pattern matched
        if (dp.startsWith("(\\d{1,2})/(\\d{1,2})")) {
          // MM/DD/YYYY
          const idx = m.indexOf(m[0]);
          return new Date(parseInt(m[3]), parseInt(m[1]) - 1, parseInt(m[2]));
        } else if (dp.startsWith("(\\d{1,2})-(\\d{1,2})")) {
          // MM-DD-YYYY
          return new Date(parseInt(m[3]), parseInt(m[1]) - 1, parseInt(m[2]));
        } else if (dp.startsWith("(\\d{4})-(\\d{2})")) {
          // YYYY-MM-DD
          return new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
        } else {
          // Month DD, YYYY
          const monthStr = m[1].toLowerCase().replace(/\.$/, "");
          const monthNum = monthMap[monthStr];
          if (monthNum !== undefined) {
            return new Date(parseInt(m[3]), monthNum, parseInt(m[2]));
          }
        }
      }
    }
  }
  return null;
}

/**
 * Redact explicitly labeled service/encounter dates. Only matches dates that
 * follow a recognized service date label (Date of Service, Admission Date, etc.).
 */
function scrubServiceDates(text: string): string {
  // Label patterns — longest first within each category
  const labels = [
    "Date of Service",
    "Date of Admission",
    "Date of Discharge",
    "Date of Encounter",
    "Date of Visit",
    "Date of Procedure",
    "Date of Surgery",
    "Date of Injury",
    "Date of Onset",
    "Date of Referral",
    "Date of Authorization",
    "Service Date",
    "Encounter Date",
    "Visit Date",
    "Admission Date",
    "Admit Date",
    "Discharge Date",
    "Procedure Date",
    "Surgery Date",
    "Injury Date",
    "Onset Date",
    "Referral Date",
    "Date Signed",
    "Date Reviewed",
    // Signature block "Date:" — match only when at start of line or after whitespace
    "Date",
  ];
  const labelAlt = labels.join("|");

  // Reuse same date format patterns as DOB
  const monthFull =
    "January|February|March|April|May|June|July|August|September|October|November|December";
  const monthAbbr = "Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec";
  const monthAny = `(?:${monthFull}|${monthAbbr})`;

  const datePatterns = [
    `\\d{1,2}/\\d{1,2}/\\d{4}`,
    `\\d{1,2}-\\d{1,2}-\\d{4}`,
    `\\d{1,2}\\.\\d{1,2}\\.\\d{4}`,
    `\\d{4}-\\d{2}-\\d{2}`,
    `${monthAny}\\.?\\s+\\d{1,2},?\\s+\\d{4}`,
    `\\d{1,2}\\s+${monthAny}\\.?\\s+\\d{4}`,
  ];
  const dateAlt = datePatterns.join("|");

  // Combined: label + separator + date
  // Use word boundary before label to avoid matching "Update Date" inside "Updated"
  const pattern = new RegExp(
    `(?:^|(?<=[\\s(\\n]))((?:${labelAlt})[:\\s]\\s*)(${dateAlt})`,
    "gim"
  );

  return text.replace(pattern, `$1${SERVICE_DATE_PLACEHOLDER}`);
}

// ============================================================================
// Narrative Date Offsetting — converts remaining specific dates to relative offsets
// ============================================================================

/**
 * Find remaining specific dates in the text (not already redacted) and replace
 * them with relative offsets from the Date of Service.
 *
 * "drawn on 03/04/2026" → "drawn on [14 days prior to encounter]"
 * "as of 03/04/2026"    → "as of [14 days prior to encounter]"
 *
 * Only targets fully specified dates (MM/DD/YYYY, Month DD YYYY, etc.).
 * Partial dates like "January 2025" or relative phrases like "4 years ago"
 * are left untouched.
 */
function scrubNarrativeDates(text: string, referenceDate: Date | null): string {
  if (!referenceDate) return text;

  const monthMap: Record<string, number> = {
    january: 0,
    february: 1,
    march: 2,
    april: 3,
    may: 4,
    june: 5,
    july: 6,
    august: 7,
    september: 8,
    october: 9,
    november: 10,
    december: 11,
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
  };

  function daysBetween(a: Date, b: Date): number {
    const msPerDay = 86400000;
    // Normalize to midnight UTC
    const utcA = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
    const utcB = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
    return Math.round((utcB - utcA) / msPerDay);
  }

  function formatOffset(days: number): string {
    const absDays = Math.abs(days);
    if (days === 0) return "[date of encounter]";

    let magnitude: string;
    if (absDays < 7) {
      magnitude = absDays === 1 ? "1 day" : `${absDays} days`;
    } else if (absDays < 30) {
      const weeks = Math.round(absDays / 7);
      magnitude = weeks === 1 ? "~1 week" : `~${weeks} weeks`;
    } else if (absDays < 365) {
      const months = Math.round(absDays / 30);
      magnitude = months === 1 ? "~1 month" : `~${months} months`;
    } else {
      const years = Math.round(absDays / 365);
      magnitude = years === 1 ? "~1 year" : `~${years} years`;
    }

    return days < 0
      ? `[${magnitude} after encounter]`
      : `[${magnitude} prior to encounter]`;
  }

  function parseAndReplace(match: string, ...groups: string[]): string {
    // Skip if inside a placeholder
    if (match.includes("[[")) return match;

    let date: Date | null = null;

    // Try MM/DD/YYYY
    let m = match.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) {
      date = new Date(parseInt(m[3]), parseInt(m[1]) - 1, parseInt(m[2]));
    }

    // Try MM-DD-YYYY
    if (!date) {
      m = match.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
      if (m)
        date = new Date(parseInt(m[3]), parseInt(m[1]) - 1, parseInt(m[2]));
    }

    // Try MM.DD.YYYY
    if (!date) {
      m = match.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
      if (m)
        date = new Date(parseInt(m[3]), parseInt(m[1]) - 1, parseInt(m[2]));
    }

    // Try YYYY-MM-DD
    if (!date) {
      m = match.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m)
        date = new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
    }

    // Try Month DD, YYYY or Month DD YYYY
    if (!date) {
      m = match.match(/^([A-Za-z]+)\.?\s+(\d{1,2}),?\s+(\d{4})$/);
      if (m) {
        const monthStr = m[1].toLowerCase().replace(/\.$/, "");
        const monthNum = monthMap[monthStr];
        if (monthNum !== undefined) {
          date = new Date(parseInt(m[3]), monthNum, parseInt(m[2]));
        }
      }
    }

    // Try DD Month YYYY
    if (!date) {
      m = match.match(/^(\d{1,2})\s+([A-Za-z]+)\.?\s+(\d{4})$/);
      if (m) {
        const monthStr = m[2].toLowerCase().replace(/\.$/, "");
        const monthNum = monthMap[monthStr];
        if (monthNum !== undefined) {
          date = new Date(parseInt(m[3]), monthNum, parseInt(m[1]));
        }
      }
    }

    if (!date || isNaN(date.getTime())) return match;

    const days = daysBetween(date, referenceDate!);
    return formatOffset(days);
  }

  const monthFull =
    "January|February|March|April|May|June|July|August|September|October|November|December";
  const monthAbbr = "Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec";
  const monthAny = `(?:${monthFull}|${monthAbbr})`;

  // Match fully specified dates only
  const datePatterns = [
    `\\d{1,2}/\\d{1,2}/\\d{4}`,
    `\\d{1,2}-\\d{1,2}-\\d{4}`,
    `\\d{1,2}\\.\\d{1,2}\\.\\d{4}`,
    `\\d{4}-\\d{2}-\\d{2}`,
    `${monthAny}\\.?\\s+\\d{1,2},?\\s+\\d{4}`,
    `\\d{1,2}\\s+${monthAny}\\.?\\s+\\d{4}`,
  ];

  const combined = new RegExp(`(${datePatterns.join("|")})`, "gi");

  return text.replace(combined, parseAndReplace);
}

// ============================================================================
// MRN Redaction — runs BEFORE name detection
// ============================================================================

const MRN_PLACEHOLDER = "[[MRN]]";

/**
 * Redact explicitly labeled medical record numbers. Only matches values
 * that follow a recognized MRN/chart/account label. Unlabeled numbers
 * are never touched.
 */
function scrubMRN(text: string): string {
  // Label patterns — longest first to avoid partial matches.
  // Escaped dots for "No." patterns.
  const labels = [
    "Medical Record Number",
    "Medical Record No\\.",
    "Medical Record No",
    "Medical Rec",
    "Med Rec",
    "Chart Number",
    "Chart No\\.",
    "Chart No",
    "Chart#",
    "Account Number",
    "Account No\\.",
    "Account No",
    "Patient Account",
    "Patient ID",
    "MR Number",
    "MRN#",
    "MR#",
    "MRN",
    "Acct#",
    "Acct",
  ];
  const labelAlt = labels.join("|");

  // Value patterns — alphanumeric MRN formats
  const valuePatterns = [
    // Alphanumeric prefix + digits with optional hyphens: MR4471829, PT-4471829, A4471829
    `[A-Za-z]{1,3}-?\\d[\\d-]{3,14}\\d`,
    // Pure digits with hyphens or spaces: 447-182-9, 447 1829, 44-71829
    `\\d[\\d\\- ]{3,14}\\d`,
    // Plain digits 4-12: 4471829, 00447182900
    `\\d{4,12}`,
  ];
  const valueAlt = valuePatterns.join("|");

  // Combined: label + separator + value
  // Separator: # (already in some labels), colon + optional space, or just space
  // The label itself may end with # (MRN#, Chart#, Acct#) — separator handles the rest
  const pattern = new RegExp(
    `((?:${labelAlt})[:#\\s]\\s*)(${valueAlt})(?=\\s|[),;.|\\]]|$)`,
    "gi"
  );

  return text.replace(pattern, `$1${MRN_PLACEHOLDER}`);
}

// ============================================================================
// Phone / Fax Redaction — runs BEFORE name detection
// ============================================================================

const PHONE_PLACEHOLDER = "[[PHONE]]";
const FAX_PLACEHOLDER = "[[FAX]]";

/**
 * Redact phone and fax numbers. Unlike DOB/MRN, phone patterns are
 * distinctive enough to match without a label. When a fax label is
 * present, uses [[FAX]]; otherwise [[PHONE]].
 */
function scrubPhoneNumbers(text: string): string {
  // Extension pattern (optional, appended after any phone format)
  const ext = `(?:\\s*(?:ext\\.?|x|extension)\\s*\\d{1,6})?`;

  // Core phone number patterns (ordered most specific → least specific)
  const phonePatterns = [
    // +1 (555) 555-5555 or +1 555-555-5555
    `\\+1\\s*\\(?\\d{3}\\)?[\\s.-]?\\d{3}[\\s.-]?\\d{4}`,
    // 1-555-555-5555
    `1-\\d{3}-\\d{3}-\\d{4}`,
    // (555) 555-5555 or (555)555-5555
    `\\(\\d{3}\\)\\s?\\d{3}[\\s.-]\\d{4}`,
    // 555-555-5555 or 555.555.5555 or 555 555 5555
    `\\d{3}[\\s.-]\\d{3}[\\s.-]\\d{4}`,
    // 5555555555 (10 consecutive digits — must not be preceded by digit/letter)
    `(?<![\\d\\w])\\d{10}(?![\\d\\w])`,
    // 555-5555 (7-digit local — with separator required)
    `(?<![\\d])\\d{3}-\\d{4}(?!\\d)`,
  ];
  const phoneAlt = phonePatterns.map((p) => `(?:${p})`).join("|");

  // Full pattern with optional extension
  const fullPhone = `(?:${phoneAlt})${ext}`;

  // Optional label patterns
  const faxLabels = `Fax|Facsimile`;
  const phoneLabels = `Phone|Ph|Tel|Telephone|Cell|Mobile|Home|Work|Office|Contact|Callback|Call\\s*back|Pager`;
  const allLabels = `${faxLabels}|${phoneLabels}`;

  // Strategy: two passes.
  // Pass 1: labeled phone/fax — preserve label, tag appropriately.
  // Pass 2: unlabeled phone patterns — replace with [[PHONE]].

  // Pass 1: Fax-labeled
  const faxRe = new RegExp(
    `((?:${faxLabels})[:#]?\\s*(?:number\\s*)?[:#]?\\s*)(${fullPhone})`,
    "gi"
  );
  let result = text.replace(faxRe, `$1${FAX_PLACEHOLDER}`);

  // Pass 1b: Phone-labeled (non-fax)
  const phoneLabelRe = new RegExp(
    `((?:${phoneLabels})[:#]?\\s*(?:number\\s*)?[:#]?\\s*)(${fullPhone})`,
    "gi"
  );
  result = result.replace(phoneLabelRe, `$1${PHONE_PLACEHOLDER}`);

  // Pass 2: Unlabeled phone patterns (not already replaced)
  // Only match if not already inside a placeholder tag and not preceded by a non-phone label
  const nonPhoneLabels =
    /(?:NPI|EIN|TIN|SSN|DEA|License|Lic|Ref|Reference|Policy|Group|Claim|Auth|Case)[:#]?\s*$/i;
  const unlabeledRe = new RegExp(`(${fullPhone})`, "g");
  result = result.replace(unlabeledRe, (match, _p1, offset) => {
    // Don't re-replace if already inside a placeholder tag
    const before = result.slice(Math.max(0, offset - 12), offset);
    if (/\[\[(?:PHONE|FAX|MRN|DOB)\]\]/.test(before)) return match;
    if (match.includes("[[")) return match;
    // Don't match if preceded by a non-phone label
    const before30 = result.slice(Math.max(0, offset - 30), offset);
    if (nonPhoneLabels.test(before30)) return match;
    return PHONE_PLACEHOLDER;
  });

  return result;
}

// ============================================================================
// SSN Redaction — runs BEFORE name detection
// ============================================================================

const SSN_PLACEHOLDER = "[[SSN]]";

/**
 * Validate SSN digits against IRS rules to reduce false positives.
 * Area (first 3) cannot be 000, 666, or 900-999.
 * Group (middle 2) cannot be 00. Serial (last 4) cannot be 0000.
 * Known test SSNs are excluded.
 */
function isValidSSN(area: string, group: string, serial: string): boolean {
  const a = parseInt(area, 10);
  const g = parseInt(group, 10);
  const s = parseInt(serial, 10);
  if (a === 0 || a === 666 || a >= 900) return false;
  if (g === 0) return false;
  if (s === 0) return false;
  // Known test SSNs
  const full = `${area}-${group}-${serial}`;
  if (full === "078-05-1120" || full === "219-09-9999") return false;
  return true;
}

/**
 * Redact Social Security Numbers. Labeled SSNs always match.
 * Unlabeled formatted SSNs (XXX-XX-XXXX) match on pattern alone.
 * Unlabeled unformatted 9-digit sequences only match with a label.
 */
function scrubSSN(text: string): string {
  const labels = [
    "Social Security Number",
    "Social Security No\\.",
    "Social Security No",
    "Social Security",
    "Soc Sec",
    "SS\\s*#",
    "SSN",
  ];
  const labelAlt = labels.join("|");

  // Formatted SSN patterns
  const ssnHyphen = `(\\d{3})-(\\d{2})-(\\d{4})`; // 123-45-6789
  const ssnSpace = `(\\d{3})\\s(\\d{2})\\s(\\d{4})`; // 123 45 6789
  const ssnPlain = `(\\d{9})`; // 123456789

  let result = text;

  // Pass 1: Labeled SSNs — match all formats including unformatted 9-digit
  const labeledHyphen = new RegExp(
    `((?:${labelAlt})[:#]?\\s*)${ssnHyphen}(?=\\s|[),;.|\\]]|$)`,
    "gi"
  );
  result = result.replace(
    labeledHyphen,
    (match, label, area, group, serial) => {
      if (!isValidSSN(area, group, serial)) return match;
      return label + SSN_PLACEHOLDER;
    }
  );

  const labeledSpace = new RegExp(
    `((?:${labelAlt})[:#]?\\s*)${ssnSpace}(?=\\s|[),;.|\\]]|$)`,
    "gi"
  );
  result = result.replace(labeledSpace, (match, label, area, group, serial) => {
    if (!isValidSSN(area, group, serial)) return match;
    return label + SSN_PLACEHOLDER;
  });

  // Labeled unformatted 9-digit — only with label
  const labeledPlain = new RegExp(
    `((?:${labelAlt})[:#]?\\s*)${ssnPlain}(?=\\s|[),;.|\\]]|$)`,
    "gi"
  );
  result = result.replace(labeledPlain, (match, label, digits) => {
    const area = digits.slice(0, 3);
    const group = digits.slice(3, 5);
    const serial = digits.slice(5, 9);
    if (!isValidSSN(area, group, serial)) return match;
    return label + SSN_PLACEHOLDER;
  });

  // Pass 2: Unlabeled formatted SSNs (XXX-XX-XXXX only — distinctive enough)
  // Must not be preceded by another digit or already inside a placeholder
  const unlabeledHyphen = /(?<!\d)(\d{3})-(\d{2})-(\d{4})(?!\d)/g;
  result = result.replace(
    unlabeledHyphen,
    (match, area, group, serial, offset) => {
      // Skip if already inside a placeholder
      const before = result.slice(Math.max(0, offset - 8), offset);
      if (/\[\[/.test(before)) return match;
      // Skip if preceded by an SSN label (already handled in Pass 1)
      const before30 = result.slice(Math.max(0, offset - 30), offset);
      if (new RegExp(`(?:${labelAlt})[:#]?\\s*$`, "i").test(before30))
        return match;
      if (!isValidSSN(area, group, serial)) return match;
      return SSN_PLACEHOLDER;
    }
  );

  return result;
}

// ============================================================================
// Email Redaction — runs BEFORE name detection
// ============================================================================

const EMAIL_PLACEHOLDER = "[[EMAIL]]";

/**
 * Redact email addresses. Matches with or without a label.
 * Runs before name detection so local-part names (john.smith@) don't
 * trigger the Name Region Parser.
 */
function scrubEmail(text: string): string {
  const emailPattern = `[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}`;
  const emailLabels = `Email\\s*Address|E-?[Mm]ail|Contact`;

  // Pass 1: Labeled emails — preserve label
  const labeledRe = new RegExp(
    `((?:${emailLabels})[:#]?\\s*)(${emailPattern})`,
    "gi"
  );
  let result = text.replace(labeledRe, `$1${EMAIL_PLACEHOLDER}`);

  // Pass 2: Unlabeled emails
  const unlabeledRe = new RegExp(`(${emailPattern})`, "g");
  result = result.replace(unlabeledRe, (match, _p1, offset) => {
    // Don't re-replace if already inside a placeholder
    const before = result.slice(Math.max(0, offset - 10), offset);
    if (/\[\[/.test(before)) return match;
    if (match.includes("[[")) return match;
    return EMAIL_PLACEHOLDER;
  });

  return result;
}

// ============================================================================
// URL Redaction — runs BEFORE name detection
// ============================================================================

const URL_PLACEHOLDER = "[[URL]]";

/**
 * Redact web URLs. Matches protocol URLs, www URLs, and bare domains
 * with recognized TLDs. Runs after email redaction so email addresses
 * (already replaced) don't interfere.
 */
function scrubURLs(text: string): string {
  // Common TLDs for bare domain matching
  const tlds = "com|org|net|edu|gov|io|health|info|co|us|uk|ca|au|app|dev|med";

  // URL path/query/fragment (shared across patterns)
  const urlTail = `(?:[/?#][^\\s<>\"'()]*)?`;

  // Pattern 1: Full URLs with protocol — http:// https:// ftp://
  const protocolUrl = `(?:https?|ftp)://[a-zA-Z0-9](?:[a-zA-Z0-9.-]*[a-zA-Z0-9])?(?::\\d+)?${urlTail}`;

  // Pattern 2: www URLs — www.example.com/path
  const wwwUrl = `www\\.[a-zA-Z0-9](?:[a-zA-Z0-9.-]*[a-zA-Z0-9])?${urlTail}`;

  // Pattern 3: Bare domains with recognized TLD — example.com/path
  // Must have alphabetic chars on both sides of at least one dot, end with known TLD
  // Supports subdomains: patientportal.mercy.org, docs.api.dev
  const bareDomain = `[a-zA-Z][a-zA-Z0-9-]*(?:\\.[a-zA-Z][a-zA-Z0-9-]*)*\\.(?:${tlds})(?:\\.[a-zA-Z]{2,})?${urlTail}`;

  // Combine: protocol first (most specific), then www, then bare
  const combined = new RegExp(
    `(?:${protocolUrl}|${wwwUrl}|${bareDomain})`,
    "gi"
  );

  return text.replace(combined, (match, offset) => {
    // Guard: skip if inside an already-replaced placeholder
    const before = text.slice(Math.max(0, offset - 8), offset);
    if (/\[\[/.test(before)) return match;

    // Guard: skip if preceded by @ (email local@domain — already handled)
    if (offset > 0 && text[offset - 1] === "@") return match;

    // Guard: skip decimal numbers like "98.6" or "4.8"
    // A bare domain match starting with digits is actually a decimal
    if (/^\d/.test(match)) return match;

    // Guard: skip clinical abbreviations — single letter + period patterns
    // These won't match protocol/www patterns, but could theoretically match bare
    if (/^[A-Z]\.[A-Z]\./i.test(match)) return match;

    return URL_PLACEHOLDER;
  });
}

// ============================================================================
// Address Redaction — runs BEFORE name detection
// ============================================================================

const ADDRESS_PLACEHOLDER = "[[ADDRESS]]";
const ZIP_PLACEHOLDER = "[[ZIP]]";
const CITY_PLACEHOLDER = "[[CITY]]";

// State abbreviations (uppercase, for matching in original text)
const STATE_ABBREVS = new Set([
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "DC",
  "PR",
  "GU",
  "VI",
  "AS",
  "MP",
]);

// Full state names (title case, for matching)
const STATE_NAMES_LIST = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
  "District of Columbia",
];
const STATE_NAMES_SET = new Set(STATE_NAMES_LIST.map((s) => s.toLowerCase()));

/**
 * Sub-pattern A: Street addresses.
 * [Number] [optional direction] [Street Name words] [Street Type] [optional Unit]
 */
function scrubStreetAddresses(text: string): string {
  // Street types — word boundary enforced in the regex
  const streetTypes =
    "Street|Avenue|Boulevard|Drive|Lane|Road|Court|Place|Way|Circle|" +
    "Parkway|Highway|Terrace|Loop|Trail|Pass|Run|Row|Path|Pike|Alley|Plaza|" +
    "St|Ave|Blvd|Ln|Rd|Ct|Pl|Cir|Pkwy|Hwy|Ter|Tr";

  // Unit suffixes
  const unitPart = `(?:\\s*,?\\s*(?:Apt|Apartment|Unit|Suite|Ste|Bldg|Building|Fl|Floor|#)\\.?\\s*[A-Za-z0-9-]+)?`;

  // Direction prefix
  const dir = `(?:(?:N|S|E|W|NE|NW|SE|SW)\\.?\\s+)?`;

  // Street name: 1-5 words (letters, periods, hyphens) before the street type
  const streetName = `(?:[A-Za-z][A-Za-z.''-]*\\s+){1,5}`;

  // Address labels (optional, preserved)
  const addrLabels = `(?:(?:Home\\s+)?Address|Addr|Mailing\\s+Address|Residence|Resides\\s+at|Lives\\s+at)[:\\s]+`;

  // Full pattern
  const re = new RegExp(
    `((?:${addrLabels})?)` + // $1: label
      `(\\d+[A-Za-z]?\\s+${dir}${streetName}` + // number + dir + name
      `(?:${streetTypes})\\.?` + // street type
      `(?=\\s|,|$|[)]|\\n)` + // word boundary after street type
      `${unitPart})`, // unit
    "gm"
  );

  return text.replace(re, (match, label) => {
    return (label || "") + ADDRESS_PLACEHOLDER;
  });
}

/**
 * Sub-pattern B: Zip codes.
 * 5-digit or ZIP+4, only when preceded by a state or zip label.
 */
function scrubZipCodes(text: string): string {
  const zip = `\\d{5}(?:-\\d{4})?`;

  // Labeled: ZIP: 97034
  const labeledRe = new RegExp(
    `((?:ZIP|Zip)(?:\\s*Code)?[:\\s]+)(${zip})(?=\\s|[,;.)|\\]]|$)`,
    "gi"
  );
  let result = text.replace(labeledRe, `$1${ZIP_PLACEHOLDER}`);

  // After 2-letter state abbreviation: OR 97034
  const abbrList = [...STATE_ABBREVS].join("|");
  const afterAbbrRe = new RegExp(
    `(?<=(?:${abbrList})\\s)(${zip})(?=\\s|[,;.)|\\]]|$)`,
    "g"
  );
  result = result.replace(afterAbbrRe, ZIP_PLACEHOLDER);

  // After full state name: Oregon 97034
  const nameList = STATE_NAMES_LIST.map(escapeRegex).join("|");
  const afterNameRe = new RegExp(
    `((?:${nameList})\\s+)(${zip})(?=\\s|[,;.)|\\]]|$)`,
    "gi"
  );
  result = result.replace(afterNameRe, `$1${ZIP_PLACEHOLDER}`);

  return result;
}

/**
 * Sub-pattern C: City + State combinations (US only).
 * [City words], [State] → [[CITY]], State
 */
function scrubCityState(text: string): string {
  let result = text;

  // City words: one or more capitalized words ending at a comma
  // Use a pattern that captures everything up to ", STATE"
  const cityWords = `[A-Z][a-zA-Z]*(?:\\s+[A-Z][a-zA-Z]*){0,3}`;

  // Match: City, StateAbbreviation (2-letter uppercase)
  const abbrList = [...STATE_ABBREVS].join("|");
  const cityAbbrRe = new RegExp(
    `(?:^|(?<=[\\s(\\n]))(${cityWords})(,\\s*)(${abbrList})(?=\\s|\\d|[,;.)|\\]]|$)`,
    "gm"
  );
  // Professional credential abbreviations that overlap with state abbreviations.
  // MD = Maryland vs Medical Doctor, PA = Pennsylvania vs Physician Assistant, etc.
  const CREDENTIAL_STATE_OVERLAP = new Set([
    "MD",
    "DO",
    "PA",
    "MA",
    "ME",
    "AL",
    "NP",
    "DC",
    "NE",
  ]);
  result = result.replace(cityAbbrRe, (match, city, comma, state) => {
    if (city.includes("[[")) return match;
    // Guard: If the "state" is a professional credential abbreviation (MD, DO, PA, etc.),
    // check context to determine if this is "Surname, Credential" vs "City, State".
    if (
      CREDENTIAL_STATE_OVERLAP.has(state) &&
      PROFESSIONAL_SUFFIXES.has(state.toLowerCase())
    ) {
      const beforeIdx = result.indexOf(match);
      const before60 = result
        .slice(Math.max(0, beforeIdx - 60), beforeIdx)
        .toLowerCase();
      // Strong name context: preceded by a title, name label, or role descriptor
      const hasNameContext =
        /(?:dr\.?|doctor|provider|physician|signed by|name:|patient:|referring|attending|supervising|nurse|practitioner)\s*$/i.test(
          before60
        );
      if (hasNameContext) return match;
      // Check if preceded by a [[NAME]] placeholder (name already partially scrubbed)
      const before30 = result.slice(Math.max(0, beforeIdx - 30), beforeIdx);
      if (/\[\[NAME\]\][.\s]*$/.test(before30)) return match;
      // If the line starts with a structural label like "Provider:" that suggests
      // a person follows, skip city redaction
      const lineStart = result.lastIndexOf("\n", beforeIdx) + 1;
      const linePrefix = result
        .slice(lineStart, beforeIdx)
        .toLowerCase()
        .trim();
      if (
        /^(?:provider|physician|attending|referring|signed|documented|ordered)/.test(
          linePrefix
        )
      )
        return match;
      // Signature block: line starts with the match (no label), preceded by a
      // horizontal rule or blank line — common for "Name, MD" at bottom of notes
      if (/(?:_{3,}|—{3,}|-{3,})\s*$/.test(before30)) return match;
    }
    return CITY_PLACEHOLDER + comma + state;
  });

  // Match: City, Full State Name
  const nameList = STATE_NAMES_LIST.map(escapeRegex).join("|");
  const cityNameRe = new RegExp(
    `(?:^|(?<=[\\s(\\n]))(${cityWords})(,\\s*)(${nameList})(?=\\s|[,;.)|\\]]|$)`,
    "gim"
  );
  result = result.replace(cityNameRe, (match, city, comma, state) => {
    if (city.includes("[[")) return match;
    if (!STATE_NAMES_SET.has(state.toLowerCase())) return match;
    return CITY_PLACEHOLDER + comma + state;
  });

  return result;
}

// ============================================================================
// Health Plan Beneficiary Number (Insurance ID) Redaction
// ============================================================================

const INSURANCE_ID_PLACEHOLDER = "[[INSURANCE_ID]]";

/**
 * Redact insurance member IDs, policy numbers, group numbers, and similar
 * health plan identifiers. Labeled matches only — these identifiers have
 * no distinctive unlabeled format.
 */
function scrubInsuranceID(text: string): string {
  // Labels — longest first within each category
  const labels = [
    "Subscriber Number",
    "Subscriber No\\.",
    "Subscriber No",
    "Subscriber ID",
    "Subscriber#",
    "Member Number",
    "Member No\\.",
    "Member No",
    "Member ID",
    "Member#",
    "Policy Number",
    "Policy No\\.",
    "Policy No",
    "Policy#",
    "Group Number",
    "Group No\\.",
    "Group No",
    "Group ID",
    "Group \\#",
    "Group#",
    "Group",
    "Plan Number",
    "Plan No\\.",
    "Plan No",
    "Plan ID",
    "Insurance No\\.",
    "Insurance No",
    "Insurance ID",
    "Beneficiary No\\.",
    "Beneficiary No",
    "Beneficiary ID",
    "Certificate Number",
    "Certificate No\\.",
    "Certificate No",
    "Insured ID",
    "Enrollee ID",
    "ID Number",
  ];
  const labelAlt = labels.join("|");

  // Value patterns — alphanumeric insurance ID formats
  const valuePatterns = [
    // Alphanumeric with hyphens: 123-456-789-00, WPA-993-0021
    `[A-Za-z0-9][A-Za-z0-9\\- ]{3,19}[A-Za-z0-9]`,
    // Pure alphanumeric 4-20 chars: XYZ123456789, H12345678, 12345678A
    `[A-Za-z0-9]{4,20}`,
  ];
  const valueAlt = valuePatterns.join("|");

  // Combined: label + separator + value
  const pattern = new RegExp(
    `((?:${labelAlt})[:#]?\\s*)(${valueAlt})(?=\\s|[,;.)|\\]]|$)`,
    "gi"
  );

  return text.replace(pattern, (match, label, value) => {
    // Guard: skip if the value is purely non-numeric text (e.g. "Group: Kaiser HMO")
    // Insurance IDs must contain at least one digit
    if (!/\d/.test(value)) return match;

    // Guard: skip if this looks like a prior auth reference
    // (These are PeerPrep-relevant — preserve them)
    if (/^PA[-\s]/i.test(value)) return match;

    return label + INSURANCE_ID_PLACEHOLDER;
  });
}

// ============================================================================
// Account Number Redaction
// ============================================================================

const ACCOUNT_PLACEHOLDER = "[[ACCOUNT]]";

/**
 * Redact financial/billing account numbers, encounter numbers, visit numbers,
 * case numbers, and claim numbers. Labeled matches only.
 * Runs after MRN and Insurance ID — any overlapping labels already claimed
 * by earlier passes are already replaced.
 */
function scrubAccountNumbers(text: string): string {
  const labels = [
    "Guarantor Account",
    "Billing Account",
    "Account Number",
    "Account No\\.",
    "Account No",
    "Account#",
    "Acct No\\.",
    "Acct No",
    "Acct#",
    "Acct",
    "Billing No\\.",
    "Billing No",
    "Billing#",
    "Financial Number",
    "Financial No\\.",
    "Financial No",
    "Guarantor No\\.",
    "Guarantor No",
    "Encounter Number",
    "Encounter No\\.",
    "Encounter No",
    "Encounter ID",
    "Encounter#",
    "Visit Number",
    "Visit No\\.",
    "Visit No",
    "Visit ID",
    "Visit#",
    "Case Number",
    "Case No\\.",
    "Case No",
    "Case ID",
    "Case#",
    "Claim Number",
    "Claim No\\.",
    "Claim No",
    "Claim#",
  ];
  const labelAlt = labels.join("|");

  const valuePatterns = [
    `[A-Za-z0-9][A-Za-z0-9\\- ]{3,16}[A-Za-z0-9]`,
    `[A-Za-z0-9]{4,15}`,
  ];
  const valueAlt = valuePatterns.join("|");

  const pattern = new RegExp(
    `((?:${labelAlt})[:#]?\\s*)(${valueAlt})(?=\\s|[,;.)|\\]]|$)`,
    "gi"
  );

  return text.replace(pattern, (match, label, value) => {
    if (!/\d/.test(value)) return match;
    if (/^PA[-\s]/i.test(value)) return match;
    // Skip if already a placeholder
    if (value.includes("[[")) return match;
    return label + ACCOUNT_PLACEHOLDER;
  });
}

// ============================================================================
// Device Identifier Redaction
// ============================================================================

const DEVICE_ID_PLACEHOLDER = "[[DEVICE_ID]]";

/**
 * Redact serial numbers, UDIs, lot numbers, and unique device identifiers.
 * Labeled matches only — device IDs have no distinctive unlabeled format.
 */
function scrubDeviceIDs(text: string): string {
  const labels = [
    "Unique Device Identifier",
    "Serial Number",
    "Serial No\\.",
    "Serial No",
    "Serial#",
    "Generator Serial",
    "Generator ID",
    "Pump Serial",
    "Pump ID",
    "Lead Serial",
    "Lead ID",
    "Device No\\.",
    "Device No",
    "Device ID",
    "Model Number",
    "Model No\\.",
    "Model No",
    "Model#",
    "Lot Number",
    "Lot No\\.",
    "Lot No",
    "Lot#",
    "Implant No",
    "Implant ID",
    "Sensor No",
    "Sensor ID",
    "Catheter No",
    "Catheter ID",
    "S/N",
    "SN",
    "UDI",
  ];
  const labelAlt = labels.join("|");

  // Value patterns
  const valuePatterns = [
    // UDI GS1 format: (01)00643169007222(17)260301...
    `\\(01\\)[A-Za-z0-9()]{10,80}`,
    // Alphanumeric with hyphens/slashes/periods: PM-2026-00441, ICD/SN-44782901
    `[A-Za-z0-9][A-Za-z0-9/\\-\\.]{3,29}[A-Za-z0-9]`,
    // Short alphanumeric 4-30: MDT12345678, 9X2B4K, ABC12345
    `[A-Za-z0-9]{4,30}`,
  ];
  const valueAlt = valuePatterns.join("|");

  // REF needs special handling — only match when followed by alphanumeric device-like value
  // not "ref #PA-" or narrative use
  const reLabels = `(?:${labelAlt}|REF)`;

  const pattern = new RegExp(
    `((?:${reLabels})[:#]?\\s*)(${valueAlt})(?=\\s|[,;.)|\\]]|$)`,
    "gi"
  );

  return text.replace(pattern, (match, label, value) => {
    // Must contain at least one digit (not a pure text model name)
    if (!/\d/.test(value)) return match;
    // Skip prior auth references
    if (/^PA[-\s]/i.test(value)) return match;
    // Skip already-replaced placeholders
    if (value.includes("[[")) return match;
    return label + DEVICE_ID_PLACEHOLDER;
  });
}

// ============================================================================
// NPI / Provider Identifier Redaction
// ============================================================================

const NPI_PLACEHOLDER = "[[NPI]]";
const DEA_PLACEHOLDER = "[[DEA]]";

/**
 * Redact NPI numbers, DEA numbers, EIN/TIN, and license numbers.
 * NPI is always exactly 10 digits. DEA is 2 letters + 7 digits.
 * These directly identify individual providers.
 */
function scrubProviderIDs(text: string): string {
  const labels = [
    "National Provider Identifier",
    "NPI Number",
    "NPI No\\.",
    "NPI No",
    "NPI#",
    "NPI",
    "DEA Number",
    "DEA No\\.",
    "DEA No",
    "DEA#",
    "DEA",
    "EIN",
    "TIN",
    "Tax ID",
    "Tax Identification Number",
    "License Number",
    "License No\\.",
    "License No",
    "License#",
    "Lic\\.?\\s*No\\.",
    "Lic\\.?\\s*No",
    "Lic\\.?#",
    "Lic\\.",
    "State License",
    "Medical License",
  ];
  const labelAlt = labels.join("|");

  // Value: digits, letters, hyphens (4-15 chars)
  const valueAlt = `[A-Za-z0-9][A-Za-z0-9\\-]{2,13}[A-Za-z0-9]|\\d{4,15}`;

  const pattern = new RegExp(
    `(?:^|(?<=[\\s(\\n]))((?:${labelAlt})[:#]?\\s*)(${valueAlt})(?=\\s|[,;.)|\\]]|$)`,
    "gim"
  );

  return text.replace(pattern, (match, label, value) => {
    if (!/\d/.test(value)) return match;
    if (value.includes("[[")) return match;
    // Determine placeholder based on label
    const labelLower = label.toLowerCase();
    if (/dea/.test(labelLower)) return label + DEA_PLACEHOLDER;
    return label + NPI_PLACEHOLDER;
  });
}

// ============================================================================
// Vehicle Identifier Redaction
// ============================================================================

const VEHICLE_ID_PLACEHOLDER = "[[VEHICLE_ID]]";

/**
 * Redact VINs and license plate numbers. Labeled matches only —
 * neither format is distinctive enough to match without a label.
 */
function scrubVehicleIDs(text: string): string {
  const labels = [
    "Vehicle Identification Number",
    "Vehicle ID",
    "VIN No",
    "VIN#",
    "VIN",
    "License Plate",
    "Plate No\\.",
    "Plate No",
    "Plate#",
    "Tag No\\.",
    "Tag No",
    "Registration No\\.",
    "Registration No",
    "Registration",
  ];
  const labelAlt = labels.join("|");

  // VIN: exactly 17 alphanumeric chars (excluding I, O, Q per NHTSA)
  // License plates: 2-8 alphanumeric with optional spaces/hyphens
  const valuePatterns = [
    // VIN — 17 chars, no I/O/Q
    `[A-HJ-NPR-Z0-9]{17}`,
    // Plate — 2-8 alphanumeric with spaces/hyphens: ABC 1234, 123-XYZ, AB1C234
    `[A-Za-z0-9][A-Za-z0-9 \\-]{1,7}[A-Za-z0-9]`,
    // Short plate — 2-3 chars (rare but possible)
    `[A-Za-z0-9]{2,8}`,
  ];
  const valueAlt = valuePatterns.join("|");

  const pattern = new RegExp(
    `((?:${labelAlt})[:#]?\\s*)(${valueAlt})(?=\\s|[,;.)|\\]]|$)`,
    "gi"
  );

  return text.replace(pattern, (match, label, value) => {
    if (value.includes("[[")) return match;
    // Must contain at least one digit
    if (!/\d/.test(value)) return match;
    return label + VEHICLE_ID_PLACEHOLDER;
  });
}

// ============================================================================
// IP Address Redaction
// ============================================================================

const IP_PLACEHOLDER = "[[IP_ADDRESS]]";

/**
 * Redact IPv4 and IPv6 addresses. IPv4 matches with or without a label —
 * four dot-separated octets (0-255) is distinctive enough to not collide
 * with clinical values (which use 1-3 dot-separated groups).
 * IPv6 also matches without a label.
 */
function scrubIPAddresses(text: string): string {
  const ipLabels = `IP\\s*Address|IP\\s*Addr|IPv[46]|IP`;

  // IPv4: four dot-separated groups of 0-255
  // Each octet: 25[0-5] | 2[0-4]\d | [01]?\d\d?
  const octet = `(?:25[0-5]|2[0-4]\\d|[01]?\\d\\d?)`;
  const ipv4 = `${octet}\\.${octet}\\.${octet}\\.${octet}`;

  // IPv6: 8 groups of 1-4 hex chars separated by colons, or abbreviated with ::
  const ipv6Full = `[0-9a-fA-F]{1,4}(?::[0-9a-fA-F]{1,4}){7}`;
  const ipv6Abbr = `[0-9a-fA-F]{0,4}(?::[0-9a-fA-F]{0,4}){2,7}`;
  // Abbreviated form must contain ::
  const ipv6 = `(?:${ipv6Full}|${ipv6Abbr})`;

  let result = text;

  // Pass 1: Labeled IP addresses
  const labeledRe = new RegExp(
    `((?:${ipLabels})[:#]?\\s*)(${ipv4}|${ipv6})(?=\\s|[,;.)|\\]]|$)`,
    "gi"
  );
  result = result.replace(labeledRe, `$1${IP_PLACEHOLDER}`);

  // Pass 2: Unlabeled IPv4 — the four-octet structure is distinctive
  // Must not be preceded/followed by a digit or dot (boundary check)
  const unlabeledV4 = new RegExp(`(?<![\\d.])(${ipv4})(?![\\d.])`, "g");
  result = result.replace(unlabeledV4, (match, ip, offset) => {
    // Skip if already inside a placeholder
    const before = result.slice(Math.max(0, offset - 8), offset);
    if (/\[\[/.test(before)) return match;
    // Skip if already replaced by a labeled pass
    if (match.includes("[[")) return match;
    return IP_PLACEHOLDER;
  });

  // Pass 3: Unlabeled IPv6 full form (8 colon-separated hex groups)
  const unlabeledV6 = new RegExp(`(?<![:\\w])(${ipv6Full})(?![:\\w])`, "g");
  result = result.replace(unlabeledV6, (match, ip, offset) => {
    const before = result.slice(Math.max(0, offset - 8), offset);
    if (/\[\[/.test(before)) return match;
    return IP_PLACEHOLDER;
  });

  return result;
}

// ============================================================================
// Employer Name Redaction
// ============================================================================

const EMPLOYER_PLACEHOLDER = "[[EMPLOYER]]";

// Organizational suffixes — help detect end of org name
const ORG_SUFFIXES = new Set([
  "inc",
  "llc",
  "corp",
  "corporation",
  "company",
  "co",
  "partners",
  "associates",
  "group",
  "foundation",
  "center",
  "centre",
  "clinic",
  "hospital",
  "university",
  "labs",
  "laboratories",
  "studio",
  "agency",
  "masonry",
  "engineering",
  "design",
  "services",
  "solutions",
  "systems",
  "consulting",
  "international",
  "global",
  "technologies",
  "tech",
  "industries",
  "enterprises",
  "holdings",
  "management",
  "construction",
  "landscaping",
  "electric",
  "plumbing",
  "roofing",
  "automotive",
  "pharmacy",
  "church",
  "synagogue",
  "mosque",
  "temple",
]);

/**
 * Redact employer names. Only fires when an employer context indicator
 * is present. Same organization name without employer context is preserved.
 */
function scrubEmployerNames(text: string): string {
  let result = text;

  // --- Pattern 1: Explicit label ---
  // "Employer: Meridian Engineering", "Employment: Noble Cross Design"
  const labelRe = /(?:Employer|Employment)\s*:\s*/gi;
  result = result.replace(labelRe, (labelMatch, offset) => {
    const after = result.slice(offset + labelMatch.length);
    const orgName = consumeOrgName(after);
    if (!orgName) return labelMatch;
    // Replace: preserve label, replace org name
    const fullEnd = offset + labelMatch.length + orgName.length;
    return labelMatch; // handled in next step
  });
  // Actually do the replacement properly with a combined regex
  const labelFullRe = /((?:Employer|Employment)\s*:\s*)/gi;
  const labelPositions: Array<{
    start: number;
    labelEnd: number;
    orgEnd: number;
  }> = [];
  let lm: RegExpExecArray | null;
  while ((lm = labelFullRe.exec(result)) !== null) {
    const after = result.slice(lm.index + lm[0].length);
    const orgName = consumeOrgName(after);
    if (orgName) {
      labelPositions.push({
        start: lm.index,
        labelEnd: lm.index + lm[0].length,
        orgEnd: lm.index + lm[0].length + orgName.length,
      });
    }
  }
  // Replace in reverse order
  for (let i = labelPositions.length - 1; i >= 0; i--) {
    const p = labelPositions[i];
    result =
      result.slice(0, p.labelEnd) +
      EMPLOYER_PLACEHOLDER +
      result.slice(p.orgEnd);
  }

  // --- Pattern 2: Employer context indicators ---
  // "employed at X", "works for X", "retired from X", "owned X", etc.
  const employerCues = [
    // Direct employment
    `employed\\s+(?:at|by|with)`,
    `works?\\s+(?:at|for)`,
    `working\\s+(?:at|for)`,
    `retired\\s+(?:from)`,
    `job\\s+at`,
    `career\\s+at`,
    `position\\s+at`,
    `occupation\\s+at`,
    `owned`,
    // Role-based: "employed as a [role] at", "works as a [role] at/for"
    `employed\\s+as\\s+(?:a|an)\\s+[a-z][a-z\\s]*?\\s+(?:at|for)`,
    `works?\\s+as\\s+(?:a|an)\\s+[a-z][a-z\\s]*?\\s+(?:at|for)`,
    // "[role] at/for [Org]" — requires role words before at/for
    // This is handled by pattern 3 below
  ];
  const cueAlt = employerCues.join("|");
  const cueRe = new RegExp(`((?:${cueAlt})\\s+)`, "gi");

  // Collect matches and replace in reverse
  const cuePositions: Array<{ labelEnd: number; orgEnd: number }> = [];
  let cm: RegExpExecArray | null;
  const cueReExec = new RegExp(`((?:${cueAlt})\\s+)`, "gi");
  while ((cm = cueReExec.exec(result)) !== null) {
    const after = result.slice(cm.index + cm[0].length);
    // Skip "the" before org name
    let orgStart = cm.index + cm[0].length;
    const theMatch = after.match(/^the\s+/i);
    if (theMatch) orgStart += theMatch[0].length;
    const orgText = result.slice(orgStart);
    const orgName = consumeOrgName(orgText);
    if (orgName) {
      cuePositions.push({
        labelEnd: orgStart,
        orgEnd: orgStart + orgName.length,
      });
    }
  }
  for (let i = cuePositions.length - 1; i >= 0; i--) {
    const p = cuePositions[i];
    result =
      result.slice(0, p.labelEnd) +
      EMPLOYER_PLACEHOLDER +
      result.slice(p.orgEnd);
  }

  // --- Pattern 3: "[role] at/for [Org]" in social history context ---
  // "senior claims analyst at Aetna", "landscape architect at Noble Cross Design"
  // "claims adjuster for State Farm"
  // Also family: "His son is a claims adjuster for Anthem Blue Cross"
  const roleCue = new RegExp(
    `([a-z][a-z\\s-]*(?:analyst|adjuster|engineer|architect|writer|designer|manager|` +
      `director|coordinator|supervisor|technician|mechanic|mason|carpenter|electrician|` +
      `plumber|teacher|professor|instructor|nurse|physician|doctor|therapist|counselor|` +
      `administrator|assistant|clerk|driver|operator|chef|cook|bartender|server|` +
      `representative|consultant|specialist|developer|programmer|accountant|auditor|` +
      `attorney|lawyer|paralegal|executive|officer|president|owner))` +
      `\\s+(?:at|for)\\s+`,
    "gi"
  );
  const rolePositions: Array<{ labelEnd: number; orgEnd: number }> = [];
  let rm: RegExpExecArray | null;
  while ((rm = roleCue.exec(result)) !== null) {
    const orgStart = rm.index + rm[0].length;
    // Skip "the"
    let actualStart = orgStart;
    const theM = result.slice(actualStart).match(/^the\s+/i);
    if (theM) actualStart += theM[0].length;
    const orgText = result.slice(actualStart);
    const orgName = consumeOrgName(orgText);
    if (orgName) {
      rolePositions.push({
        labelEnd: actualStart,
        orgEnd: actualStart + orgName.length,
      });
    }
  }
  for (let i = rolePositions.length - 1; i >= 0; i--) {
    const p = rolePositions[i];
    // Don't double-replace if already an [[EMPLOYER]] tag
    if (result.slice(p.labelEnd, p.orgEnd).includes("[[")) continue;
    result =
      result.slice(0, p.labelEnd) +
      EMPLOYER_PLACEHOLDER +
      result.slice(p.orgEnd);
  }

  return result;
}

/**
 * Consume an organization name starting at position 0 of the given text.
 * Walks forward through capitalized words and known org tokens, stopping
 * at sentence boundaries, lowercase non-org words, or verbs.
 * Returns the matched org name string, or null if nothing valid.
 */
function consumeOrgName(text: string): string | null {
  if (!text || (!/^[A-Z]/.test(text) && !/^[A-Z]{2,}/.test(text))) return null;

  let cursor = 0;
  let lastGoodEnd = 0;
  let wordCount = 0;
  let hitSuffix = false;
  const maxWords = 8;

  while (cursor < text.length && wordCount < maxWords) {
    const ws = text.slice(cursor).match(/^[ \t]+/);
    if (ws) cursor += ws[0].length;

    if (cursor >= text.length || /[.\n;()\[\]]/.test(text[cursor])) break;

    if (text[cursor] === ",") {
      const afterComma = text.slice(cursor + 1).trimStart();
      if (
        /^[A-Z]/.test(afterComma) &&
        !STATE_ABBREVS.has(afterComma.slice(0, 2))
      ) {
        cursor++;
        continue;
      }
      break;
    }

    const wordMatch = text.slice(cursor).match(/^[A-Za-z][A-Za-z'&.-]*/);
    if (!wordMatch) break;

    const word = wordMatch[0];
    const wordLower = word.toLowerCase();

    const stopWords = new Set([
      "and",
      "but",
      "or",
      "who",
      "that",
      "which",
      "where",
      "when",
      "while",
      "is",
      "was",
      "are",
      "were",
      "has",
      "had",
      "have",
      "does",
      "did",
      "with",
      "from",
      "about",
      "after",
      "before",
      "during",
      "since",
      "he",
      "she",
      "they",
      "his",
      "her",
      "their",
      "a",
      "an",
      "reviewed",
      "documented",
      "evaluated",
      "assessed",
      "reported",
      "noted",
      "denied",
      "states",
      "says",
      "reports",
      "presented",
    ]);

    if (wordCount === 0 && !/^[A-Z]/.test(word)) break;

    if (wordCount > 0 && /^[a-z]/.test(word)) {
      const orgConnectors = new Set([
        "of",
        "the",
        "and",
        "&",
        "for",
        "at",
        "in",
        "de",
        "la",
        "del",
      ]);
      if (orgConnectors.has(wordLower)) {
        cursor += word.length;
        wordCount++;
        continue;
      }
      break;
    }

    if (wordCount > 0 && stopWords.has(wordLower)) break;

    // If we already hit a suffix, stop at the next non-suffix word
    // (allows "Design Partners" but stops before "are great")
    if (hitSuffix && !ORG_SUFFIXES.has(wordLower)) break;

    cursor += word.length;
    wordCount++;
    lastGoodEnd = cursor;

    if (
      ORG_SUFFIXES.has(wordLower) ||
      ORG_SUFFIXES.has(wordLower.replace(/\.$/, ""))
    ) {
      hitSuffix = true;
      // Don't break — allow consuming additional suffixes (e.g. "Design Partners")
    }
  }

  if (lastGoodEnd === 0 || wordCount === 0) return null;
  const name = text.slice(0, lastGoodEnd).trim();
  if (name.length < 2 || !/[A-Za-z]/.test(name)) return null;
  return name;
}

// ============================================================================
// Public API
// ============================================================================

export function scrubPHI(text: string): string {
  // Extract Date of Service from original text before any scrubbing
  const referenceDate = extractServiceDate(text);

  // Identifier redactions run first — before name detection
  let working = scrubDOB(text);
  working = scrubServiceDates(working);
  working = scrubMRN(working);
  working = scrubPhoneNumbers(working);
  working = scrubSSN(working);
  working = scrubEmail(working);
  working = scrubURLs(working);
  working = scrubStreetAddresses(working);
  working = scrubZipCodes(working);
  working = scrubCityState(working);
  working = scrubInsuranceID(working);
  working = scrubAccountNumbers(working);
  working = scrubDeviceIDs(working);
  working = scrubProviderIDs(working);
  working = scrubVehicleIDs(working);
  working = scrubIPAddresses(working);
  working = scrubEmployerNames(working);
  working = scrubFacilityNames(working);
  working = scrubNarrativeDates(working, referenceDate);
  // Name detection runs on pre-redacted text
  const result = scrubNames(working);
  if (text && text.trim().length > 0) {
    console.group("🛡️ PHI Scrubber");
    console.log("Input:", text);
    console.log("Output:", result);
    if (result !== text) {
      const nameCount = (result.match(/\[\[NAME\]\]/g) || []).length;
      const dobCount = (result.match(/\[\[DOB\]\]/g) || []).length;
      const mrnCount = (result.match(/\[\[MRN\]\]/g) || []).length;
      const phoneCount = (result.match(/\[\[PHONE\]\]/g) || []).length;
      const faxCount = (result.match(/\[\[FAX\]\]/g) || []).length;
      const ssnCount = (result.match(/\[\[SSN\]\]/g) || []).length;
      const emailCount = (result.match(/\[\[EMAIL\]\]/g) || []).length;
      const urlCount = (result.match(/\[\[URL\]\]/g) || []).length;
      const addrCount = (result.match(/\[\[ADDRESS\]\]/g) || []).length;
      const zipCount = (result.match(/\[\[ZIP\]\]/g) || []).length;
      const cityCount = (result.match(/\[\[CITY\]\]/g) || []).length;
      const insCount = (result.match(/\[\[INSURANCE_ID\]\]/g) || []).length;
      const acctCount = (result.match(/\[\[ACCOUNT\]\]/g) || []).length;
      const devCount = (result.match(/\[\[DEVICE_ID\]\]/g) || []).length;
      const vehCount = (result.match(/\[\[VEHICLE_ID\]\]/g) || []).length;
      const ipCount = (result.match(/\[\[IP_ADDRESS\]\]/g) || []).length;
      const empCount = (result.match(/\[\[EMPLOYER\]\]/g) || []).length;
      const npiCount = (result.match(/\[\[NPI\]\]/g) || []).length;
      const deaCount = (result.match(/\[\[DEA\]\]/g) || []).length;
      const facCount = (result.match(/\[\[FACILITY\]\]/g) || []).length;
      const dateCount = (result.match(/\[\[DATE\]\]/g) || []).length;
      console.log(
        `Redactions: ${nameCount} name(s), ${dobCount} DOB(s), ${dateCount} date(s), ${mrnCount} MRN(s), ${phoneCount} phone(s), ${faxCount} fax(es), ${ssnCount} SSN(s), ${emailCount} email(s), ${urlCount} URL(s), ${addrCount} addr(s), ${zipCount} ZIP(s), ${cityCount} city(s), ${insCount} ins(s), ${acctCount} acct(s), ${devCount} device(s), ${vehCount} vehicle(s), ${ipCount} IP(s), ${empCount} employer(s), ${npiCount} NPI(s), ${deaCount} DEA(s), ${facCount} facility(s) replaced`
      );
    } else {
      console.log("No PHI detected");
    }
    console.groupEnd();
  }
  return result;
}

export const scrubText = scrubPHI;

export function containsObviousPHI(text: string): boolean {
  if (!text || text.trim().length === 0) return false;
  return scrubPHI(text) !== text;
}

export async function checkScrubberHealth(): Promise<{
  healthy: boolean;
  message: string;
}> {
  try {
    const t = scrubPHI("Dr. Jane Smith is a 54-year-old female.");
    const ok =
      t.includes(NAME_PLACEHOLDER) &&
      !t.includes("Jane") &&
      !t.includes("Smith");
    return {
      healthy: ok,
      message: ok ? "Scrubber operational." : "Scrubber unhealthy.",
    };
  } catch (e) {
    return {
      healthy: false,
      message: `Error: ${e instanceof Error ? e.message : String(e)}`,
    };
  }
}

export default scrubPHI;
